// src/contexts/ThemeContext.tsx
import { createContext, useContext } from "react";

// src/themes/neobrutalism/Bubble.tsx
import { motion as motion2 } from "framer-motion";
import clsx21 from "clsx";

// src/renderers/components/MessageBody.tsx
import { useMemo as useMemo15, useRef as useRef6 } from "react";
import clsx20 from "clsx";

// src/utils/artifactParser.ts
var ARTIFACT_REGEX = /###\s+'([^']+)'\s*[\r\n]+(`{3,})([^\n\r]*)[\r\n]([\s\S]*?)\2/g;
function normalizeInfoString(info) {
  const trimmed = (info || "").trim();
  if (!trimmed) return "text";
  const first = trimmed.split(/\s+/)[0];
  return first || "text";
}
var parseArtifacts = (text) => {
  if (!text) return [];
  const segments = [];
  let lastIndex = 0;
  ARTIFACT_REGEX.lastIndex = 0;
  let match;
  while ((match = ARTIFACT_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        content: text.slice(lastIndex, match.index)
      });
    }
    segments.push({
      type: "file",
      path: match[1],
      language: normalizeInfoString(match[3]),
      content: match[4] ?? "",
      raw: match[0]
    });
    lastIndex = ARTIFACT_REGEX.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({
      type: "text",
      content: text.slice(lastIndex)
    });
  }
  return segments;
};

// src/renderers/messageBlocks/blockId.ts
function fnv1a(input) {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}
function blockIdFromIndex(messageId, kind, index, seed) {
  return `v1_${kind}_${index}_${fnv1a(`${messageId}|${kind}|${index}|${seed}`)}`;
}
function blockIdFromParts(messageId, kind, index, seed) {
  return blockIdFromIndex(messageId, kind, index, seed);
}

// src/renderers/messageBlocks/normalizeMessage.ts
function normalizeMessage(input) {
  const { messageId, role, parts, content, isStreaming } = input;
  const blocks = [];
  const pushMarkdown = (md, idxSeed) => {
    const trimmed = md ?? "";
    if (!trimmed) return;
    const b = {
      id: blockIdFromParts(messageId, "markdown", blocks.length, idxSeed),
      kind: "markdown",
      messageId,
      role,
      markdown: trimmed,
      selectable: true,
      isStreaming
    };
    blocks.push(b);
  };
  const pushArtifact = (seg, idxSeed) => {
    const b = {
      id: blockIdFromParts(messageId, "artifact", blocks.length, idxSeed),
      kind: "artifact",
      messageId,
      role,
      path: seg.path,
      language: seg.language || "text",
      content: seg.content ?? "",
      raw: seg.raw,
      selectable: true
    };
    blocks.push(b);
  };
  const processTextWithArtifacts = (text) => {
    const segments = parseArtifacts(text || "");
    segments.forEach((seg, i) => {
      if (seg.type === "text") {
        pushMarkdown(seg.content, `text:${i}:${seg.content.slice(0, 80)}`);
      } else {
        pushArtifact(seg, `artifact:${i}:${seg.path}:${seg.language}:${seg.content.slice(0, 80)}`);
      }
    });
  };
  let pendingFiles = null;
  const flushAttachments = () => {
    if (pendingFiles && pendingFiles.length > 0) {
      const b = {
        id: blockIdFromParts(
          messageId,
          "attachments",
          blocks.length,
          `attachments:${pendingFiles.map((f) => f.name).join("|").slice(0, 120)}`
        ),
        kind: "attachments",
        messageId,
        role,
        files: pendingFiles,
        selectable: false
      };
      blocks.push(b);
      pendingFiles = null;
    }
  };
  if (Array.isArray(parts) && parts.length > 0) {
    for (const p of parts) {
      switch (p.type) {
        case "text":
          flushAttachments();
          processTextWithArtifacts(p.content || "");
          break;
        case "ui": {
          if (p.component === "file-bubble") {
            const props = p.props || {};
            const f = {
              url: props.url,
              name: props.name,
              type: props.type || "file",
              mimeType: props.mimeType,
              fileId: props.fileId
            };
            if (!pendingFiles) pendingFiles = [];
            pendingFiles.push(f);
          } else {
            flushAttachments();
            const b = {
              id: blockIdFromParts(messageId, "ui", blocks.length, `${p.component}:${p.id}`),
              kind: "ui",
              messageId,
              role,
              uiId: p.id,
              component: p.component,
              props: p.props,
              selectable: false
            };
            blocks.push(b);
          }
          break;
        }
        case "tool_call": {
          flushAttachments();
          const b = {
            id: blockIdFromParts(messageId, "tool_call", blocks.length, `${p.name}:${p.callId}`),
            kind: "tool_call",
            messageId,
            role,
            callId: p.callId,
            name: p.name,
            args: p.args || {},
            selectable: true,
            status: "success"
          };
          blocks.push(b);
          break;
        }
        case "tool_result": {
          flushAttachments();
          const b = {
            id: blockIdFromParts(messageId, "tool_result", blocks.length, `${p.callId}`),
            kind: "tool_result",
            messageId,
            role,
            callId: p.callId,
            result: p.result,
            selectable: true,
            status: "success",
            ...p.name ? { name: p.name } : {}
          };
          blocks.push(b);
          break;
        }
        case "reasoning": {
          flushAttachments();
          const b = {
            id: blockIdFromParts(messageId, "reasoning", blocks.length, `${p.content.slice(0, 120)}`),
            kind: "reasoning",
            messageId,
            role,
            content: p.content || "",
            isComplete: p.isComplete,
            selectable: true
          };
          blocks.push(b);
          break;
        }
        default:
          break;
      }
    }
    flushAttachments();
  } else {
    flushAttachments();
    processTextWithArtifacts(content || "");
  }
  const toMarkdown = () => {
    const chunks = [];
    for (const b of blocks) {
      if (b.kind === "markdown") chunks.push(b.markdown);
      else if (b.kind === "artifact") chunks.push(b.raw);
    }
    return chunks.join("\n\n").replace(/\n{4,}/g, "\n\n\n").trim();
  };
  return { blocks, toMarkdown };
}

// src/renderers/messageBlocks/blockRegistry.ts
function createBlockRegistry(defaults, overrides) {
  return { ...defaults, ...overrides || {} };
}

// src/renderers/components/blocks/MarkdownBlock.tsx
import { useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import clsx3 from "clsx";

// src/renderers/components/CodeBlock.tsx
import { useState } from "react";
import clsx from "clsx";
import { Check, Copy, Maximize2, Minus, Plus } from "lucide-react";

// src/renderers/components/CodeModal.tsx
import { createPortal } from "react-dom";
import { Download, X } from "lucide-react";
import { jsx, jsxs } from "react/jsx-runtime";
var CodeModal = ({ code, language, onClose, onDownload }) => {
  if (typeof document === "undefined") return null;
  return createPortal(
    /* @__PURE__ */ jsxs("div", { className: "fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 lg:p-10 h-screen w-screen overflow-hidden", children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "absolute inset-0 bg-slate-900/90 backdrop-blur-md animate-in fade-in duration-200",
          onClick: onClose
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "relative w-full max-w-6xl h-full max-h-[90vh] flex flex-col bg-[#0d1117] rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-700 ring-1 ring-white/10", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between px-4 py-3 bg-[#161b22] border-b border-slate-700 shrink-0", children: [
          /* @__PURE__ */ jsx("div", { className: "flex items-center gap-3", children: /* @__PURE__ */ jsxs("span", { className: "text-sm font-mono text-slate-400 font-bold uppercase tracking-wider", children: [
            language || "Text",
            " Preview"
          ] }) }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsxs(
              "button",
              {
                onClick: onDownload,
                className: "flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-md transition-colors",
                children: [
                  /* @__PURE__ */ jsx(Download, { size: 14 }),
                  /* @__PURE__ */ jsx("span", { className: "hidden sm:inline", children: "Download" })
                ]
              }
            ),
            /* @__PURE__ */ jsx("div", { className: "w-px h-4 bg-slate-700 mx-1" }),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: onClose,
                className: "p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-md transition-colors",
                title: "Close (Esc)",
                children: /* @__PURE__ */ jsx(X, { size: 18 })
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-auto p-4 md:p-8 custom-scrollbar bg-[#0d1117]", children: /* @__PURE__ */ jsx("pre", { className: "font-mono text-sm text-slate-300 leading-relaxed tab-4 w-full", children: /* @__PURE__ */ jsx("code", { children: code }) }) })
      ] })
    ] }),
    document.body
  );
};

// src/renderers/components/CodeBlock.tsx
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var CodeBlock = ({
  inline,
  className,
  children,
  collapsed,
  defaultCollapsed,
  onCollapsedChange,
  ...props
}) => {
  const match = /language-(\w+)/.exec(className || "");
  const language = match?.[1];
  const codeText = String(children ?? "").replace(/\n$/, "");
  const isMultiLine = codeText.includes("\n");
  const [copied, setCopied] = useState(false);
  const [localCollapsed, setLocalCollapsed] = useState(!!defaultCollapsed);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isCollapsed = collapsed ?? localCollapsed;
  const setCollapsed = (next) => {
    if (collapsed === void 0) {
      setLocalCollapsed(next);
    }
    onCollapsedChange?.(next);
  };
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2e3);
  };
  const handleDownload = () => {
    const blob = new Blob([codeText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `snippet.${language || "txt"}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const isSimple = inline || !isMultiLine && !language;
  if (isSimple) {
    return /* @__PURE__ */ jsx2(
      "code",
      {
        className: clsx(
          "inline align-middle break-words select-text cursor-text",
          "font-mono text-[0.85em] font-medium",
          "px-1.5 py-0.5 mx-0.5 rounded-md border backdrop-blur-[2px]",
          "bg-slate-100/60 border-slate-200/60 text-slate-700",
          "dark:bg-slate-800/60 dark:border-slate-700 dark:text-slate-100"
        ),
        ...props,
        children
      }
    );
  }
  return /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsxs2(
      "div",
      {
        className: clsx(
          "not-prose my-4 rounded-xl border border-slate-200/50 bg-[#0d1117] shadow-xl shadow-slate-200/10 group/code w-full min-w-0",
          "transition-all duration-300 ease-in-out overflow-hidden",
          isCollapsed ? "h-[46px]" : "h-auto"
        ),
        children: [
          /* @__PURE__ */ jsxs2("div", { className: "flex items-center justify-between px-4 py-3 bg-[#161b22] border-b border-white/5 select-none h-[46px]", children: [
            /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-2 group/lights", children: [
              /* @__PURE__ */ jsx2(
                "div",
                {
                  className: "w-3 h-3 rounded-full border border-black/10 shadow-sm",
                  style: { backgroundColor: "#ff5f56" },
                  "aria-hidden": true
                }
              ),
              /* @__PURE__ */ jsx2(
                "button",
                {
                  onClick: (e) => {
                    e.stopPropagation();
                    setCollapsed(!isCollapsed);
                  },
                  className: "group w-3 h-3 rounded-full border border-black/10 shadow-sm flex items-center justify-center transition-all cursor-pointer relative hover:brightness-90 active:scale-95",
                  style: { backgroundColor: "#ffbd2e" },
                  title: isCollapsed ? "Expand" : "Collapse",
                  "aria-label": isCollapsed ? "Expand code block" : "Collapse code block",
                  "aria-expanded": !isCollapsed,
                  type: "button",
                  children: isCollapsed ? /* @__PURE__ */ jsx2(
                    Plus,
                    {
                      size: 8,
                      strokeWidth: 3,
                      className: "text-black/50 opacity-80 group-hover:opacity-100 absolute pointer-events-none transition-opacity"
                    }
                  ) : /* @__PURE__ */ jsx2(
                    Minus,
                    {
                      size: 8,
                      strokeWidth: 3,
                      className: "text-black/50 opacity-80 group-hover:opacity-100 absolute pointer-events-none transition-opacity"
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx2(
                "button",
                {
                  onClick: (e) => {
                    e.stopPropagation();
                    setIsModalOpen(true);
                  },
                  className: "group w-3 h-3 rounded-full border border-black/10 shadow-sm flex items-center justify-center transition-all cursor-pointer relative hover:brightness-90 active:scale-95",
                  style: { backgroundColor: "#27c93f" },
                  title: "Fullscreen",
                  "aria-label": "Open code fullscreen",
                  type: "button",
                  children: /* @__PURE__ */ jsx2(
                    Maximize2,
                    {
                      size: 6,
                      strokeWidth: 3,
                      className: "text-black/50 opacity-0 group-hover:opacity-100 absolute pointer-events-none transition-opacity"
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx2("span", { className: "ml-3 text-xs font-mono text-slate-500 lowercase opacity-60 group-hover/code:opacity-100 transition-opacity", children: language || "text" })
            ] }),
            /* @__PURE__ */ jsxs2(
              "button",
              {
                onClick: () => handleCopy(codeText),
                className: "flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-slate-500 hover:text-white transition-colors",
                title: "Copy code",
                type: "button",
                children: [
                  copied ? /* @__PURE__ */ jsx2(Check, { size: 12, className: "text-emerald-400" }) : /* @__PURE__ */ jsx2(Copy, { size: 12 }),
                  copied ? "COPIED" : "COPY"
                ]
              }
            )
          ] }),
          !isCollapsed && /* @__PURE__ */ jsx2("div", { className: "p-4 overflow-x-auto custom-scrollbar bg-[#0d1117]", children: /* @__PURE__ */ jsx2("pre", { className: "text-[13px] font-mono text-gray-50 leading-relaxed tab-4 m-0", children: /* @__PURE__ */ jsx2("code", { className, ...props, children }) }) })
        ]
      }
    ),
    isModalOpen && /* @__PURE__ */ jsx2(
      CodeModal,
      {
        code: codeText,
        language: language || "text",
        onClose: () => setIsModalOpen(false),
        onDownload: handleDownload
      }
    )
  ] });
};

// styles/markdownClassnames.ts
import clsx2 from "clsx";
function markdownClassNames(role, opts) {
  const compact = !!opts?.compact;
  const tone = opts?.tone || (role === "user" ? "dark" : "light");
  const isUserDark = role === "user" && tone === "dark";
  return clsx2(
    "chat-md",
    "min-w-0 max-w-none",
    isUserDark ? "text-white" : "text-slate-900",
    compact ? "[&>p]:my-2" : "[&>p]:my-3",
    // links
    isUserDark ? "[&_a]:text-white [&_a]:underline [&_a:hover]:opacity-90" : "[&_a]:text-blue-600 [&_a]:no-underline hover:[&_a]:underline [&_a]:font-semibold",
    // blockquote
    isUserDark ? "[&_blockquote]:border-white/30 [&_blockquote]:text-white/85" : "[&_blockquote]:border-slate-200 [&_blockquote]:text-slate-600",
    // inline code
    isUserDark ? "[&_:not(pre)>code]:bg-white/10 [&_:not(pre)>code]:text-white" : "[&_:not(pre)>code]:bg-slate-100 [&_:not(pre)>code]:text-slate-800",
    // bullets/numbers enforcement
    "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
    "[&_li::marker]:text-current"
  );
}

// src/renderers/components/blocks/MarkdownBlock.tsx
import { useMessageUI } from "@dopomogai/chat-react";
import { jsx as jsx3 } from "react/jsx-runtime";
var MarkdownBlock = ({ block, surface }) => {
  const markdown = block.markdown || "";
  const isPossiblyUnclosedFence = useMemo(() => {
    if (!block.isStreaming) return false;
    const fenceCount = (markdown.match(/```/g) || []).length;
    return fenceCount % 2 === 1;
  }, [block.isStreaming, markdown]);
  const compact = surface === "widget";
  const { getRendererState, setRendererState } = useMessageUI(block.messageId);
  const tasksKey = `tasks:${block.id}`;
  const taskState = getRendererState(tasksKey, {});
  const checkboxIndexRef = useRef(0);
  const codeIndexRef = useRef(0);
  checkboxIndexRef.current = 0;
  codeIndexRef.current = 0;
  const getStableNodeKey = (node, prefix, fallbackIndex) => {
    const pos = node?.position?.start;
    if (pos) {
      if (typeof pos.offset === "number") return `${prefix}:offset:${pos.offset}`;
      if (typeof pos.line === "number" && typeof pos.column === "number") return `${prefix}:line:${pos.line}:${pos.column}`;
    }
    return `${prefix}:index:${fallbackIndex}`;
  };
  const PersistedCodeBlock = (props) => {
    const index = codeIndexRef.current++;
    const rendererKey = getStableNodeKey(props.node, "code", index);
    const ui = getRendererState(rendererKey, { collapsed: false });
    return /* @__PURE__ */ jsx3(
      CodeBlock,
      {
        ...props,
        collapsed: !!ui.collapsed,
        onCollapsedChange: (collapsed) => setRendererState(rendererKey, { collapsed })
      }
    );
  };
  const tone = surface === "app" && block.role === "user" ? "light" : void 0;
  if (isPossiblyUnclosedFence) {
    return /* @__PURE__ */ jsx3("div", { className: clsx3(markdownClassNames(block.role, { compact, tone }), "select-text whitespace-pre-wrap break-words"), children: markdown });
  }
  return /* @__PURE__ */ jsx3("div", { className: clsx3(markdownClassNames(block.role, { compact, tone }), "select-text"), children: /* @__PURE__ */ jsx3(
    ReactMarkdown,
    {
      remarkPlugins: [remarkGfm],
      components: {
        // Ensure list markers render regardless of host CSS
        ul: ({ node, ...props }) => /* @__PURE__ */ jsx3("ul", { ...props, className: clsx3("list-disc pl-5", props.className) }),
        ol: ({ node, ...props }) => /* @__PURE__ */ jsx3("ol", { ...props, className: clsx3("list-decimal pl-5", props.className) }),
        // Interactive task list checkboxes
        input: ({ node, ...props }) => {
          const p = props;
          if (p.type === "checkbox") {
            const index = getStableNodeKey(node, "task", checkboxIndexRef.current++);
            const defaultChecked = !!p.checked;
            const checked = taskState[index] ?? defaultChecked;
            const { disabled, ...rest } = p;
            return /* @__PURE__ */ jsx3(
              "input",
              {
                ...rest,
                type: "checkbox",
                checked,
                onChange: (e) => setRendererState(tasksKey, { [index]: e.currentTarget.checked }),
                className: clsx3("align-[-2px] mr-2 h-[14px] w-[14px] rounded border-slate-300 text-blue-600 focus:ring-0")
              }
            );
          }
          return /* @__PURE__ */ jsx3("input", { ...props });
        },
        code: PersistedCodeBlock,
        a: ({ node, ...props }) => /* @__PURE__ */ jsx3("a", { ...props, target: "_blank", rel: "noopener noreferrer" }),
        img: ({ node, ...props }) => /* @__PURE__ */ jsx3(
          "img",
          {
            ...props,
            className: clsx3("max-w-full h-auto rounded-xl border border-slate-200", props.className),
            loading: "lazy"
          }
        ),
        table: ({ node, ...props }) => /* @__PURE__ */ jsx3("div", { className: "w-full overflow-x-auto", children: /* @__PURE__ */ jsx3("table", { ...props }) })
      },
      children: markdown
    }
  ) });
};

// src/renderers/components/blocks/ArtifactBlock.tsx
import { useMemo as useMemo3 } from "react";
import clsx7 from "clsx";
import { FileText } from "lucide-react";
import { useMessageUI as useMessageUI2 } from "@dopomogai/chat-react";

// src/renderers/components/BlockFrame.tsx
import { useMemo as useMemo2, useState as useState2 } from "react";
import clsx5 from "clsx";
import { Minus as Minus2, Square, X as XIcon } from "lucide-react";

// src/renderers/components/FullscreenModal.tsx
import { useEffect } from "react";
import { createPortal as createPortal2 } from "react-dom";
import clsx4 from "clsx";
import { X as X2 } from "lucide-react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var FullscreenModal = ({
  title,
  isOpen,
  onClose,
  children,
  className,
  contentClassName
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);
  if (!isOpen || typeof document === "undefined") return null;
  return createPortal2(
    /* @__PURE__ */ jsxs3("div", { className: clsx4("fixed inset-0 z-[9999]", className), children: [
      /* @__PURE__ */ jsx4(
        "button",
        {
          "aria-label": "Close fullscreen",
          onClick: onClose,
          className: "absolute inset-0 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-150 cursor-default"
        }
      ),
      /* @__PURE__ */ jsx4("div", { className: "absolute inset-0 p-3 md:p-6 flex", children: /* @__PURE__ */ jsxs3(
        "div",
        {
          className: clsx4(
            "relative w-full h-full rounded-2xl border border-white/10 bg-slate-900/70 shadow-2xl overflow-hidden",
            "animate-in zoom-in-95 duration-150",
            "flex flex-col",
            contentClassName
          ),
          children: [
            /* @__PURE__ */ jsxs3("div", { className: "flex items-center justify-between px-4 py-3 border-b border-white/10 bg-slate-900/50", children: [
              /* @__PURE__ */ jsx4("div", { className: "min-w-0 text-sm font-semibold text-white/90 truncate", children: title }),
              /* @__PURE__ */ jsx4(
                "button",
                {
                  onClick: onClose,
                  className: clsx4(
                    "select-none",
                    "p-2 rounded-lg",
                    "text-white/70 hover:text-white hover:bg-white/10",
                    "transition-colors"
                  ),
                  title: "Close (Esc)",
                  children: /* @__PURE__ */ jsx4(X2, { size: 16 })
                }
              )
            ] }),
            /* @__PURE__ */ jsx4("div", { className: "flex-1 min-h-0 overflow-auto custom-scrollbar p-4", children })
          ]
        }
      ) })
    ] }),
    document.body
  );
};

// src/renderers/components/BlockFrame.tsx
import { Fragment as Fragment2, jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var BlockFrame = ({
  title,
  subtitle,
  badge,
  children,
  actions,
  defaultCollapsed = false,
  collapsible = true,
  onDelete,
  allowFullscreen = true,
  className,
  headerClassName,
  bodyClassName,
  blockId,
  onCollapseChange
}) => {
  const [collapsed, setCollapsed] = useState2(defaultCollapsed);
  const [fullscreen, setFullscreen] = useState2(false);
  const canCollapse = collapsible;
  const canFullscreen = allowFullscreen;
  const canDelete = !!onDelete;
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        onCollapseChange?.(next);
      } catch {
      }
      return next;
    });
  };
  const header = useMemo2(() => {
    return /* @__PURE__ */ jsxs4(
      "div",
      {
        className: clsx5(
          "flex items-center justify-between gap-3",
          "px-3 py-2",
          "border-b border-slate-200",
          "bg-white/70 backdrop-blur",
          headerClassName
        ),
        children: [
          /* @__PURE__ */ jsxs4("div", { className: "flex items-center gap-3 min-w-0", children: [
            /* @__PURE__ */ jsxs4("div", { className: "flex items-center gap-1.5 select-none", children: [
              canCollapse && /* @__PURE__ */ jsx5(
                "button",
                {
                  type: "button",
                  onClick: toggleCollapsed,
                  className: clsx5(
                    "h-3 w-3 rounded-full bg-amber-400 hover:bg-amber-500",
                    "shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]",
                    "flex items-center justify-center",
                    "transition-colors pointer-events-auto"
                  ),
                  title: collapsed ? "Expand" : "Collapse",
                  children: /* @__PURE__ */ jsx5(Minus2, { size: 9, className: "text-black/50" })
                }
              ),
              canFullscreen && /* @__PURE__ */ jsx5(
                "button",
                {
                  type: "button",
                  onClick: () => setFullscreen(true),
                  className: clsx5(
                    "h-3 w-3 rounded-full bg-emerald-400 hover:bg-emerald-500",
                    "shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]",
                    "flex items-center justify-center",
                    "transition-colors pointer-events-auto"
                  ),
                  title: "Fullscreen",
                  children: /* @__PURE__ */ jsx5(Square, { size: 7, className: "text-black/50" })
                }
              ),
              canDelete && /* @__PURE__ */ jsx5(
                "button",
                {
                  type: "button",
                  onClick: onDelete,
                  className: clsx5(
                    "h-3 w-3 rounded-full bg-rose-400 hover:bg-rose-500",
                    "shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]",
                    "flex items-center justify-center",
                    "transition-colors pointer-events-auto"
                  ),
                  title: "Delete block",
                  children: /* @__PURE__ */ jsx5(XIcon, { size: 9, className: "text-black/50" })
                }
              )
            ] }),
            /* @__PURE__ */ jsxs4("div", { className: "min-w-0", children: [
              /* @__PURE__ */ jsxs4("div", { className: "flex items-center gap-2 min-w-0", children: [
                /* @__PURE__ */ jsx5("div", { className: "text-xs font-semibold text-slate-800 truncate", children: title }),
                badge && /* @__PURE__ */ jsx5("div", { className: "shrink-0", children: badge })
              ] }),
              subtitle && /* @__PURE__ */ jsx5("div", { className: "text-[11px] text-slate-500 truncate", children: subtitle })
            ] })
          ] }),
          /* @__PURE__ */ jsx5("div", { className: "shrink-0 select-none pointer-events-auto", children: actions })
        ]
      }
    );
  }, [
    title,
    subtitle,
    badge,
    actions,
    headerClassName,
    collapsed,
    canCollapse,
    canFullscreen,
    canDelete,
    onDelete
  ]);
  const body = /* @__PURE__ */ jsx5(
    "div",
    {
      className: clsx5(
        "select-text",
        "px-3 py-3",
        bodyClassName
      ),
      children
    }
  );
  return /* @__PURE__ */ jsxs4(Fragment2, { children: [
    /* @__PURE__ */ jsxs4(
      "div",
      {
        className: clsx5(
          "w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden",
          className
        ),
        "data-block-id": blockId,
        children: [
          header,
          !collapsed && body
        ]
      }
    ),
    /* @__PURE__ */ jsx5(
      FullscreenModal,
      {
        isOpen: fullscreen,
        onClose: () => setFullscreen(false),
        title,
        contentClassName: "bg-slate-900/80",
        children: /* @__PURE__ */ jsx5("div", { className: "rounded-xl border border-white/10 bg-white/5 p-4", children })
      }
    )
  ] });
};

// src/renderers/components/BlockActions.tsx
import React5 from "react";
import clsx6 from "clsx";
import { Copy as Copy2, WrapText, Check as Check2 } from "lucide-react";

// src/utils/copy.ts
async function copyText(text) {
  const t = (text ?? "").toString();
  if (!t) return;
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(t);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = t;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  ta.style.top = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}
async function copyFromElementText(el) {
  if (!el) return;
  const text = (el.innerText || "").trim();
  if (!text) return;
  await copyText(text);
}

// src/renderers/components/BlockActions.tsx
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var BlockActions = ({
  getCopyMarkdown,
  getCopyText,
  wrap,
  onToggleWrap,
  size = "xs",
  className
}) => {
  const [copied, setCopied] = React5.useState(false);
  const p = size === "xs" ? "p-1.5" : "p-2";
  const icon = size === "xs" ? 14 : 16;
  const doCopy = async () => {
    try {
      const txt = getCopyText?.()?.trim();
      const md = getCopyMarkdown?.()?.trim();
      await copyText(txt || md || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1e3);
    } catch (e) {
      console.error("Block copy failed", e);
    }
  };
  return /* @__PURE__ */ jsxs5("div", { className: clsx6("flex items-center gap-1 select-none", className), children: [
    (getCopyMarkdown || getCopyText) && /* @__PURE__ */ jsx6(
      "button",
      {
        type: "button",
        onClick: doCopy,
        className: clsx6(
          p,
          "rounded-md",
          "text-slate-500 hover:text-slate-900 hover:bg-slate-100",
          "transition-colors",
          "pointer-events-auto"
        ),
        title: "Copy",
        children: copied ? /* @__PURE__ */ jsx6(Check2, { size: icon, className: "text-emerald-600" }) : /* @__PURE__ */ jsx6(Copy2, { size: icon })
      }
    ),
    onToggleWrap && /* @__PURE__ */ jsx6(
      "button",
      {
        type: "button",
        onClick: onToggleWrap,
        className: clsx6(
          p,
          "rounded-md",
          wrap ? "text-blue-600 bg-blue-50 hover:bg-blue-100" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100",
          "transition-colors",
          "pointer-events-auto"
        ),
        title: wrap ? "Disable wrap" : "Wrap lines",
        children: /* @__PURE__ */ jsx6(WrapText, { size: icon })
      }
    )
  ] });
};

// src/renderers/components/blocks/ArtifactBlock.tsx
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var ArtifactBlock = ({ block, surface }) => {
  const { getRendererState, setRendererState } = useMessageUI2(block.messageId);
  const key = `artifact:${block.id}`;
  const ui = getRendererState(key, {
    wrap: false,
    collapsed: false,
    viewMode: "code"
  });
  const setUi = (patch) => {
    setRendererState(key, patch);
  };
  const title = useMemo3(() => {
    return /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-2 min-w-0", children: [
      /* @__PURE__ */ jsx7(FileText, { size: 14, className: "text-blue-500 shrink-0" }),
      /* @__PURE__ */ jsx7("span", { className: "truncate font-mono", children: block.path })
    ] });
  }, [block.path]);
  const subtitle = useMemo3(() => {
    const lang = (block.language || "text").toLowerCase();
    const lineCount = block.content ? block.content.split("\n").length.toLocaleString() : "0";
    return /* @__PURE__ */ jsxs6("span", { className: "text-[11px] text-slate-500", children: [
      lang,
      block.content ? ` \u2022 ${lineCount} lines` : ""
    ] });
  }, [block.language, block.content]);
  const copyAsMarkdown = useMemo3(() => {
    if (block.raw && block.raw.trim()) return block.raw;
    const lang = (block.language || "").trim();
    const fence = "```";
    return [
      `### ${block.path}`,
      `${fence}${lang}`,
      block.content || "",
      fence
    ].join("\n");
  }, [block.raw, block.path, block.language, block.content]);
  const copyContentOnly = useMemo3(() => (block.content || "").toString(), [block.content]);
  return /* @__PURE__ */ jsx7(
    BlockFrame,
    {
      blockId: block.id,
      title,
      subtitle,
      badge: /* @__PURE__ */ jsx7("span", { className: "inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 select-none", children: "artifact" }),
      allowFullscreen: surface !== "widget",
      collapsible: true,
      defaultCollapsed: !!ui.collapsed,
      onCollapseChange: (collapsed) => setUi({ collapsed }),
      actions: /* @__PURE__ */ jsx7(
        BlockActions,
        {
          getCopyText: () => copyContentOnly,
          getCopyMarkdown: () => copyAsMarkdown,
          wrap: !!ui.wrap,
          onToggleWrap: () => setUi({ wrap: !ui.wrap })
        }
      ),
      className: clsx7(surface === "widget" && "rounded-lg"),
      bodyClassName: clsx7("p-0", "bg-[#0d1117]"),
      children: /* @__PURE__ */ jsx7("div", { className: "w-full min-w-0", children: /* @__PURE__ */ jsx7("div", { className: clsx7("overflow-x-auto custom-scrollbar", ui.wrap ? "whitespace-pre-wrap" : "whitespace-pre"), children: /* @__PURE__ */ jsx7("pre", { className: "m-0 p-4 text-[13px] leading-relaxed text-gray-50 font-mono", children: /* @__PURE__ */ jsx7("code", { className: clsx7("language-" + (block.language || "text")), children: block.content || "" }) }) }) })
    }
  );
};

// src/renderers/components/blocks/UIBlock.tsx
import { useMemo as useMemo4 } from "react";
import clsx8 from "clsx";
import { LayoutGrid } from "lucide-react";

// src/renderers/GenerativeUIRenderer.tsx
import { useGenerativeUI } from "@dopomogai/chat-react";
import { Fragment as Fragment3, jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
var GenerativeUIRenderer = ({ componentName, props, fallback }) => {
  const { getComponent } = useGenerativeUI();
  const Component = getComponent(componentName);
  if (!Component) {
    if (process.env.NODE_ENV === "development") {
      return /* @__PURE__ */ jsxs7("div", { className: "p-2 border border-dashed border-red-400 rounded bg-red-50 text-xs font-mono text-red-800", children: [
        /* @__PURE__ */ jsx8("strong", { children: "Missing UI:" }),
        " ",
        componentName,
        /* @__PURE__ */ jsx8("pre", { children: JSON.stringify(props, null, 2) })
      ] });
    }
    return /* @__PURE__ */ jsx8(Fragment3, { children: fallback });
  }
  return /* @__PURE__ */ jsx8(Component, { ...props });
};

// src/renderers/components/blocks/UIBlock.tsx
import { jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
var UIBlock = ({ block, surface }) => {
  const title = useMemo4(() => {
    return /* @__PURE__ */ jsxs8("div", { className: "flex items-center gap-2 min-w-0", children: [
      /* @__PURE__ */ jsx9(LayoutGrid, { size: 14, className: "text-slate-400 shrink-0" }),
      /* @__PURE__ */ jsx9("span", { className: "truncate", children: block.component })
    ] });
  }, [block.component]);
  return /* @__PURE__ */ jsx9(
    BlockFrame,
    {
      blockId: block.id,
      title,
      subtitle: /* @__PURE__ */ jsx9("span", { className: "text-[11px] text-slate-500", children: "Interactive widget" }),
      badge: /* @__PURE__ */ jsx9("span", { className: "inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 select-none", children: "ui" }),
      allowFullscreen: surface !== "widget",
      collapsible: false,
      className: clsx8("bg-white", surface === "widget" && "rounded-lg"),
      bodyClassName: "p-3",
      children: /* @__PURE__ */ jsx9("div", { className: "min-w-0", children: /* @__PURE__ */ jsx9(GenerativeUIRenderer, { componentName: block.component, props: block.props }) })
    }
  );
};

// src/renderers/components/blocks/ToolCallBlock.tsx
import { useMemo as useMemo5 } from "react";
import clsx9 from "clsx";
import { TerminalSquare } from "lucide-react";
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var ToolCallBlock = ({ block, surface }) => {
  const title = useMemo5(() => {
    return /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-2 min-w-0", children: [
      /* @__PURE__ */ jsx10(TerminalSquare, { size: 14, className: "text-slate-400 shrink-0" }),
      /* @__PURE__ */ jsx10("span", { className: "truncate", children: block.name })
    ] });
  }, [block.name]);
  const copyText2 = useMemo5(() => {
    try {
      return JSON.stringify(block.args || {}, null, 2);
    } catch {
      return String(block.args);
    }
  }, [block.args]);
  const badge = useMemo5(() => {
    const status = block.status || "success";
    if (status === "executing" || status === "pending") {
      return /* @__PURE__ */ jsx10("span", { className: "inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-700", children: status });
    }
    if (status === "error") {
      return /* @__PURE__ */ jsx10("span", { className: "inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700", children: "error" });
    }
    return /* @__PURE__ */ jsx10("span", { className: "inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600", children: "call" });
  }, [block.status]);
  return /* @__PURE__ */ jsx10(
    BlockFrame,
    {
      blockId: block.id,
      title,
      subtitle: /* @__PURE__ */ jsx10("span", { className: "text-[11px] text-slate-500", children: "Tool call arguments" }),
      badge,
      allowFullscreen: surface !== "widget",
      collapsible: true,
      actions: /* @__PURE__ */ jsx10(BlockActions, { getCopyText: () => copyText2 }),
      className: clsx9(surface === "widget" && "rounded-lg"),
      children: /* @__PURE__ */ jsx10("div", { className: "min-w-0", children: /* @__PURE__ */ jsx10("div", { className: "rounded-lg border border-slate-200 bg-slate-50 p-3 overflow-x-auto custom-scrollbar", children: /* @__PURE__ */ jsx10("pre", { className: "text-xs font-mono text-slate-700 whitespace-pre-wrap break-words select-text m-0", children: copyText2 }) }) })
    }
  );
};

// src/renderers/components/blocks/ToolResultBlock.tsx
import { useMemo as useMemo8 } from "react";
import clsx12 from "clsx";
import { Blocks, AlertCircle as AlertCircle2 } from "lucide-react";
import { useRegistry } from "@dopomogai/chat-react";

// src/renderers/ToolBubble.tsx
import { useMemo as useMemo7, useRef as useRef3, useState as useState4 } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Terminal,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Loader2 as Loader22,
  ArrowRightLeft,
  Copy as CopyIcon,
  Check as Check3
} from "lucide-react";
import clsx11 from "clsx";

// src/renderers/FileBubble.tsx
import { useEffect as useEffect2, useMemo as useMemo6, useRef as useRef2, useState as useState3 } from "react";
import { createPortal as createPortal3 } from "react-dom";
import {
  FileText as FileText2,
  Download as Download2,
  Image as ImageIcon,
  X as X3,
  Eye,
  Loader2,
  Trash2,
  FileCode,
  AlertTriangle,
  RotateCcw
} from "lucide-react";
import clsx10 from "clsx";
import { useChat, useChatClient, useFileMetadata } from "@dopomogai/chat-react";
import { Fragment as Fragment4, jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
var IMG_TTL_MS = 5 * 60 * 1e3;
var IMG_CACHE_MAX = 60;
var imgCache = /* @__PURE__ */ new Map();
function getCachedObjectUrl(key) {
  const e = imgCache.get(key);
  if (!e) return null;
  if (Date.now() - e.ts > IMG_TTL_MS) {
    try {
      URL.revokeObjectURL(e.objectUrl);
    } catch {
    }
    imgCache.delete(key);
    return null;
  }
  return e.objectUrl;
}
function putObjectUrl(key, url) {
  const prev = imgCache.get(key);
  if (prev && prev.objectUrl !== url) {
    try {
      URL.revokeObjectURL(prev.objectUrl);
    } catch {
    }
  }
  imgCache.set(key, { objectUrl: url, ts: Date.now() });
  while (imgCache.size > IMG_CACHE_MAX) {
    const oldestKey = imgCache.keys().next().value;
    if (!oldestKey) break;
    const oldest = imgCache.get(oldestKey);
    if (oldest) {
      try {
        URL.revokeObjectURL(oldest.objectUrl);
      } catch {
      }
    }
    imgCache.delete(oldestKey);
  }
}
var AuthImage = ({ url, alt, className }) => {
  const client = useChatClient();
  const [objectUrl, setObjectUrl] = useState3(() => getCachedObjectUrl(url));
  const [loading, setLoading] = useState3(!getCachedObjectUrl(url));
  const [error, setError] = useState3(false);
  useEffect2(() => {
    let active = true;
    let localUrl = null;
    const cached = getCachedObjectUrl(url);
    if (cached) {
      setObjectUrl(cached);
      setLoading(false);
      setError(false);
      return;
    }
    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(false);
        const headers = await client.config.authProvider.getHeaders();
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error(`Failed to load image: ${res.status}`);
        const blob = await res.blob();
        localUrl = URL.createObjectURL(blob);
        putObjectUrl(url, localUrl);
        if (active) setObjectUrl(localUrl);
      } catch (e) {
        console.error("AuthImage Error:", e);
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    void fetchImage();
    return () => {
      active = false;
      if (localUrl && !imgCache.has(url)) {
        try {
          URL.revokeObjectURL(localUrl);
        } catch {
        }
      }
    };
  }, [url, client]);
  if (loading) {
    return /* @__PURE__ */ jsx11("div", { className: clsx10(className, "bg-slate-50 flex items-center justify-center animate-pulse"), children: /* @__PURE__ */ jsx11(Loader2, { size: 16, className: "animate-spin text-slate-300" }) });
  }
  if (error || !objectUrl) {
    return /* @__PURE__ */ jsx11("div", { className: clsx10(className, "bg-slate-100 flex items-center justify-center text-slate-400"), children: /* @__PURE__ */ jsx11(ImageIcon, { size: 18 }) });
  }
  return /* @__PURE__ */ jsx11("img", { src: objectUrl, alt, className });
};
var FilePreviewModal = ({ url, name, type, onClose, onDownload, useDirectUrl }) => {
  if (typeof document === "undefined") return null;
  return createPortal3(
    /* @__PURE__ */ jsxs10("div", { className: "fixed inset-0 z-[9999] flex items-center justify-center p-4", children: [
      /* @__PURE__ */ jsx11("div", { className: "absolute inset-0 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-200", onClick: onClose }),
      /* @__PURE__ */ jsx11("div", { className: "relative flex flex-col items-center justify-center w-full h-full pointer-events-none", children: /* @__PURE__ */ jsxs10("div", { className: "pointer-events-auto bg-transparent rounded-lg shadow-2xl max-w-[95vw] max-h-[95vh] w-auto h-auto flex flex-col relative animate-in zoom-in-95 duration-200", children: [
        /* @__PURE__ */ jsxs10("div", { className: "absolute top-4 right-4 flex items-center gap-2 z-50 select-none", children: [
          /* @__PURE__ */ jsx11(
            "button",
            {
              onClick: onDownload,
              className: "p-3 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-all",
              title: "Download",
              "aria-label": "Download file",
              children: /* @__PURE__ */ jsx11(Download2, { size: 20 })
            }
          ),
          /* @__PURE__ */ jsx11(
            "button",
            {
              onClick: onClose,
              className: "p-3 bg-black/50 hover:bg-red-600/80 text-white rounded-full backdrop-blur-sm transition-all",
              title: "Close",
              "aria-label": "Close preview",
              children: /* @__PURE__ */ jsx11(X3, { size: 20 })
            }
          )
        ] }),
        /* @__PURE__ */ jsx11("div", { className: "flex-1 overflow-auto flex items-center justify-center p-2", children: type === "image" ? useDirectUrl ? /* @__PURE__ */ jsx11("img", { src: url, alt: name, className: "max-w-full max-h-[90vh] object-contain rounded-md shadow-2xl" }) : /* @__PURE__ */ jsx11(AuthImage, { url, alt: name, className: "max-w-full max-h-[90vh] object-contain rounded-md shadow-2xl" }) : /* @__PURE__ */ jsxs10("div", { className: "bg-white p-12 rounded-2xl flex flex-col items-center gap-6 text-slate-400", children: [
          /* @__PURE__ */ jsx11("div", { className: "w-32 h-32 bg-slate-100 rounded-3xl flex items-center justify-center", children: /* @__PURE__ */ jsx11(FileCode, { size: 64, className: "text-slate-300" }) }),
          /* @__PURE__ */ jsxs10("div", { className: "text-center space-y-1", children: [
            /* @__PURE__ */ jsx11("p", { className: "text-slate-800 font-bold text-lg", children: name }),
            /* @__PURE__ */ jsx11("p", { className: "text-sm", children: "Preview not available for this file type." })
          ] }),
          /* @__PURE__ */ jsx11(
            "button",
            {
              onClick: onDownload,
              className: "px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold select-none",
              children: "Download File"
            }
          )
        ] }) })
      ] }) })
    ] }),
    document.body
  );
};
var FileBubble = (props) => {
  const {
    url,
    name,
    type,
    mimeType,
    size,
    onRemove,
    messageId,
    fileId,
    status = "uploaded",
    error,
    previewUrl,
    onRetry
  } = props;
  const client = useChatClient();
  const { activeId } = useChat();
  const [isDownloading, setIsDownloading] = useState3(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState3(false);
  const [isRemoving, setIsRemoving] = useState3(false);
  const [inlineError, setInlineError] = useState3(null);
  const [confirmRemove, setConfirmRemove] = useState3(false);
  const errorTimerRef = useRef2(null);
  useEffect2(() => {
    return () => {
      if (errorTimerRef.current) window.clearTimeout(errorTimerRef.current);
    };
  }, []);
  const showInlineError = (message) => {
    setInlineError(message);
    if (errorTimerRef.current) window.clearTimeout(errorTimerRef.current);
    errorTimerRef.current = window.setTimeout(() => setInlineError(null), 4e3);
  };
  const isImage = type === "image" || mimeType?.startsWith("image/");
  const canRemoveFromMessage = !!messageId && !onRemove;
  const isUploading = status === "queued" || status === "uploading";
  const isErrored = status === "error";
  const effectivePreviewUrl = previewUrl || url;
  const { metadata, isLoading: metaLoading } = useFileMetadata(fileId, { lazy: !fileId });
  const resolvedTokens = useMemo6(() => {
    const fromProps = props.tokensEstimated ?? props.tokens_estimated;
    const meta = metadata;
    const fromMeta = meta?.tokens_estimated ?? meta?.tokensEstimated;
    return fromProps ?? fromMeta ?? null;
  }, [props.tokensEstimated, props.tokens_estimated, metadata]);
  const tokensPending = !!fileId && resolvedTokens == null && !isUploading;
  const handleDownload = async (e) => {
    e?.stopPropagation();
    if (isDownloading) return;
    if (isUploading) return;
    try {
      setIsDownloading(true);
      if (previewUrl?.startsWith("blob:")) {
        const link2 = document.createElement("a");
        link2.href = previewUrl;
        link2.download = name || "download";
        document.body.appendChild(link2);
        link2.click();
        document.body.removeChild(link2);
        return;
      }
      const headers = await client.config.authProvider.getHeaders();
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error(err);
      showInlineError("Failed to download file.");
    } finally {
      setIsDownloading(false);
    }
  };
  const requestRemoveFromMessage = (e) => {
    e.stopPropagation();
    if (isRemoving) return;
    setConfirmRemove(true);
  };
  const handleRemoveFromMessage = async () => {
    if (!messageId) return;
    const convId = activeId;
    if (!convId) return;
    try {
      setIsRemoving(true);
      await client.removeFileFromConversation(convId, messageId, { fileId, url });
      setConfirmRemove(false);
    } catch (e) {
      console.error(e);
      showInlineError("Failed to update conversation.");
    } finally {
      setIsRemoving(false);
    }
  };
  return /* @__PURE__ */ jsxs10(Fragment4, { children: [
    /* @__PURE__ */ jsx11(
      "div",
      {
        className: clsx10(
          "group relative w-full max-w-[520px]",
          "bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all",
          "my-2 select-none",
          isErrored && "border-red-200 bg-red-50/30",
          isUploading && "border-blue-200"
        ),
        children: /* @__PURE__ */ jsxs10("div", { className: "flex items-center gap-3 p-3", children: [
          /* @__PURE__ */ jsxs10(
            "button",
            {
              type: "button",
              onClick: () => {
                if (!isUploading) setIsPreviewOpen(true);
                if (isUploading && previewUrl && isImage) setIsPreviewOpen(true);
              },
              className: clsx10(
                "relative shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50",
                "w-12 h-12",
                "hover:border-blue-300 transition-colors",
                (isUploading || isErrored) && "hover:border-slate-200"
              ),
              title: isUploading ? "Uploading..." : "Preview",
              "aria-label": "Preview file",
              children: [
                isImage ? previewUrl && isUploading ? /* @__PURE__ */ jsx11("img", { src: previewUrl, alt: name, className: "w-full h-full object-cover" }) : /* @__PURE__ */ jsx11(AuthImage, { url, alt: name, className: "w-full h-full object-cover" }) : /* @__PURE__ */ jsx11("div", { className: "w-full h-full flex items-center justify-center text-slate-300", children: /* @__PURE__ */ jsx11(FileText2, { size: 20 }) }),
                isUploading && /* @__PURE__ */ jsx11("div", { className: "absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center", children: /* @__PURE__ */ jsx11(Loader2, { size: 16, className: "animate-spin text-blue-500" }) }),
                isErrored && /* @__PURE__ */ jsx11("div", { className: "absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center", children: /* @__PURE__ */ jsx11(AlertTriangle, { size: 16, className: "text-red-500" }) }),
                !isUploading && !isErrored && /* @__PURE__ */ jsx11("div", { className: "absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/5 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100", children: /* @__PURE__ */ jsx11("div", { className: "bg-white/90 backdrop-blur rounded-full p-1.5 shadow text-slate-700", children: /* @__PURE__ */ jsx11(Eye, { size: 14 }) }) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs10("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsx11("div", { className: "text-sm font-semibold text-slate-800 truncate select-text", title: name, children: name }),
            /* @__PURE__ */ jsxs10("div", { className: "mt-0.5 text-[11px] text-slate-400 font-bold uppercase tracking-wide flex items-center gap-2 select-none", children: [
              /* @__PURE__ */ jsx11("span", { children: isImage ? "IMAGE" : mimeType?.split("/").pop() || "FILE" }),
              size && /* @__PURE__ */ jsxs10("span", { className: "font-normal text-slate-300", children: [
                "\u2022 ",
                size
              ] }),
              resolvedTokens != null ? /* @__PURE__ */ jsxs10("span", { className: "font-normal text-slate-300", children: [
                "\u2022 ~",
                Number(resolvedTokens).toLocaleString(),
                " tokens"
              ] }) : tokensPending ? /* @__PURE__ */ jsxs10("span", { className: "font-normal text-slate-300 flex items-center gap-1", children: [
                "\u2022 ~\u2026 tokens ",
                metaLoading && /* @__PURE__ */ jsx11(Loader2, { size: 12, className: "animate-spin text-slate-300" })
              ] }) : null,
              isUploading && /* @__PURE__ */ jsx11("span", { className: "text-blue-500", children: "\u2022 UPLOADING" }),
              isErrored && /* @__PURE__ */ jsx11("span", { className: "text-red-500", children: "\u2022 FAILED" })
            ] }),
            isErrored && /* @__PURE__ */ jsx11("div", { className: "mt-1 text-[11px] text-red-600 line-clamp-2 select-text", children: error || "Upload failed" }),
            inlineError && /* @__PURE__ */ jsx11("div", { className: "mt-1 text-[11px] text-red-600 select-text", children: inlineError }),
            confirmRemove && canRemoveFromMessage && /* @__PURE__ */ jsxs10("div", { className: "mt-2 flex items-center gap-2 text-[11px] text-slate-500", children: [
              /* @__PURE__ */ jsx11("span", { children: "Remove from message?" }),
              /* @__PURE__ */ jsx11(
                "button",
                {
                  type: "button",
                  onClick: handleRemoveFromMessage,
                  className: "px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide",
                  children: "Remove"
                }
              ),
              /* @__PURE__ */ jsx11(
                "button",
                {
                  type: "button",
                  onClick: () => setConfirmRemove(false),
                  className: "px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wide",
                  children: "Cancel"
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs10("div", { className: "shrink-0 flex items-center gap-1 select-none", children: [
            !isErrored && /* @__PURE__ */ jsx11(
              "button",
              {
                onClick: handleDownload,
                disabled: isDownloading || isUploading,
                className: clsx10(
                  "p-2 rounded-xl transition-colors",
                  isUploading ? "text-slate-300 cursor-not-allowed" : "text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                ),
                title: isUploading ? "Uploading..." : "Download",
                "aria-label": "Download file",
                children: isDownloading ? /* @__PURE__ */ jsx11(Loader2, { size: 16, className: "animate-spin text-blue-500" }) : /* @__PURE__ */ jsx11(Download2, { size: 16 })
              }
            ),
            isErrored && onRetry && /* @__PURE__ */ jsx11(
              "button",
              {
                onClick: (e) => {
                  e.stopPropagation();
                  onRetry();
                },
                className: "p-2 text-red-600 hover:bg-red-100 rounded-xl transition-colors",
                title: "Retry upload",
                "aria-label": "Retry upload",
                children: /* @__PURE__ */ jsx11(RotateCcw, { size: 16 })
              }
            ),
            onRemove && /* @__PURE__ */ jsx11(
              "button",
              {
                onClick: (e) => {
                  e.stopPropagation();
                  onRemove();
                },
                className: "p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors",
                title: "Remove Attachment",
                "aria-label": "Remove Attachment",
                children: /* @__PURE__ */ jsx11(Trash2, { size: 16 })
              }
            ),
            !onRemove && canRemoveFromMessage && /* @__PURE__ */ jsx11(
              "button",
              {
                onClick: requestRemoveFromMessage,
                disabled: isRemoving,
                className: "p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors",
                title: "Remove from message",
                "aria-label": "Remove from message",
                children: isRemoving ? /* @__PURE__ */ jsx11(Loader2, { size: 16, className: "animate-spin text-red-500" }) : /* @__PURE__ */ jsx11(Trash2, { size: 16 })
              }
            )
          ] })
        ] })
      }
    ),
    isPreviewOpen && /* @__PURE__ */ jsx11(
      FilePreviewModal,
      {
        url: effectivePreviewUrl,
        name,
        type: isImage ? "image" : "file",
        onClose: () => setIsPreviewOpen(false),
        onDownload: () => handleDownload(),
        useDirectUrl: !!previewUrl && isUploading
      }
    )
  ] });
};

// src/renderers/ToolBubble.tsx
import { useMessageUI as useMessageUI3 } from "@dopomogai/chat-react";
import { jsx as jsx12, jsxs as jsxs11 } from "react/jsx-runtime";
function stringifyAny(x) {
  if (typeof x === "string") return x;
  try {
    return JSON.stringify(x, null, 2);
  } catch {
    return String(x);
  }
}
var CodeBlock2 = ({ label, data, colorClass }) => {
  const content = stringifyAny(data);
  return /* @__PURE__ */ jsxs11("div", { className: "mt-2", children: [
    /* @__PURE__ */ jsx12("div", { className: clsx11("text-[10px] uppercase tracking-wider font-bold mb-1", colorClass), children: label }),
    /* @__PURE__ */ jsx12("div", { className: "bg-slate-50 rounded-md border border-slate-200 p-2 overflow-x-auto", children: /* @__PURE__ */ jsx12("pre", { className: "text-xs font-mono text-slate-700 whitespace-pre-wrap break-words select-text", children: content }) })
  ] });
};
var SmartResult = ({ result }) => {
  if (result && typeof result === "object" && !Array.isArray(result) && (result.url || result.href) && result.name) {
    const url = result.url || result.href;
    const mediaType = result.media_type || result.mime_type;
    return /* @__PURE__ */ jsxs11("div", { className: "mt-3", children: [
      /* @__PURE__ */ jsx12("div", { className: "text-[10px] uppercase tracking-wider font-bold mb-1 text-emerald-600", children: "Generated File" }),
      /* @__PURE__ */ jsx12(
        FileBubble,
        {
          url,
          name: result.name,
          type: String(mediaType || "").startsWith("image") ? "image" : "file",
          mimeType: mediaType
        }
      )
    ] });
  }
  if (Array.isArray(result) && result.length > 0 && (result[0]?.pageContent || result[0]?.content)) {
    return /* @__PURE__ */ jsxs11("div", { className: "mt-3", children: [
      /* @__PURE__ */ jsxs11("div", { className: "text-[10px] uppercase tracking-wider font-bold mb-1 text-blue-600", children: [
        "Retrieved Context (",
        result.length,
        " items)"
      ] }),
      /* @__PURE__ */ jsx12("div", { className: "flex flex-col gap-2 max-h-60 overflow-y-auto custom-scrollbar border border-slate-200 rounded-lg bg-slate-50/50 p-2", children: result.map((doc, i) => /* @__PURE__ */ jsxs11(
        "div",
        {
          className: "text-xs p-2 bg-white rounded border border-slate-100 shadow-sm hover:shadow-md transition-shadow",
          children: [
            /* @__PURE__ */ jsxs11("div", { className: "flex items-center justify-between mb-1 gap-2", children: [
              /* @__PURE__ */ jsx12("span", { className: "font-bold text-slate-700 truncate", children: doc.metadata?.source || doc.source || `Document ${i + 1}` }),
              doc.metadata?.score && /* @__PURE__ */ jsxs11("span", { className: "text-[9px] bg-slate-100 text-slate-500 px-1 rounded", children: [
                Math.round(doc.metadata.score * 100),
                "%"
              ] })
            ] }),
            /* @__PURE__ */ jsx12("div", { className: "text-slate-600 line-clamp-3 leading-relaxed select-text", children: doc.pageContent || doc.content })
          ]
        },
        i
      )) })
    ] });
  }
  return /* @__PURE__ */ jsx12(CodeBlock2, { label: "Tool Output", data: result, colorClass: "text-emerald-600" });
};
var ToolBubble = ({
  name,
  args,
  result,
  status = "success",
  variant,
  compact,
  messageId,
  persistKey,
  toolCallId
}) => {
  const effectiveVariant = compact ? "compact" : variant || "default";
  const defaultExpanded = status === "executing" || status === "error";
  const safeMessageId = messageId || "__toolbubble__";
  let messageUI = null;
  try {
    messageUI = useMessageUI3(safeMessageId);
  } catch {
    messageUI = null;
  }
  const key = persistKey || (toolCallId ? `tool:${toolCallId}` : "");
  const canPersist = !!messageId && !!key && !!messageUI;
  const persisted = canPersist && messageUI ? messageUI.getRendererState(key, { expanded: defaultExpanded }) : null;
  const [localExpanded, setLocalExpanded] = useState4(defaultExpanded);
  const isExpanded = canPersist ? !!persisted?.expanded : localExpanded;
  const setExpanded = (next) => {
    if (canPersist && messageUI) {
      messageUI.setRendererState(key, { expanded: next });
    } else {
      setLocalExpanded(next);
    }
  };
  const contentRef = useRef3(null);
  const [copied, setCopied] = useState4(false);
  const statusConfig = useMemo7(() => {
    switch (status) {
      case "executing":
        return {
          icon: Loader22,
          color: "text-blue-600",
          bg: "bg-blue-50",
          border: "border-blue-200",
          spin: true
        };
      case "error":
        return {
          icon: AlertCircle,
          color: "text-red-600",
          bg: "bg-red-50",
          border: "border-red-200",
          spin: false
        };
      case "success":
        return {
          icon: CheckCircle2,
          color: "text-emerald-600",
          bg: "bg-emerald-50",
          border: "border-emerald-200",
          spin: false
        };
      default:
        return {
          icon: Terminal,
          color: "text-slate-500",
          bg: "bg-slate-50",
          border: "border-slate-200",
          spin: false
        };
    }
  }, [status]);
  const StatusIcon = statusConfig.icon;
  const padding = effectiveVariant === "compact" ? "p-2" : "p-3";
  const headerText = effectiveVariant === "compact" ? "text-xs" : "text-sm";
  const onQuickCopy = async (e) => {
    e.stopPropagation();
    try {
      await copyFromElementText(contentRef.current);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      console.error(err);
    }
  };
  const onCopyRaw = async (e) => {
    e.stopPropagation();
    const text = [
      `Tool: ${name}`,
      `Status: ${status}`,
      "",
      "Args:",
      stringifyAny(args),
      ...result !== void 0 ? ["", "Result:", stringifyAny(result)] : []
    ].join("\n");
    await copyText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return /* @__PURE__ */ jsx12("div", { className: clsx11("w-full", effectiveVariant === "compact" ? "max-w-full my-1" : "max-w-2xl my-2"), children: /* @__PURE__ */ jsxs11(
    "div",
    {
      className: clsx11(
        "rounded-xl border overflow-hidden transition-all duration-200",
        statusConfig.bg,
        statusConfig.border
      ),
      children: [
        /* @__PURE__ */ jsxs11(
          "button",
          {
            onClick: () => setExpanded(!isExpanded),
            className: clsx11(
              "w-full flex items-center justify-between cursor-pointer hover:bg-black/5 transition-colors text-left",
              padding,
              "select-none"
            ),
            type: "button",
            children: [
              /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-3 overflow-hidden min-w-0", children: [
                /* @__PURE__ */ jsx12("div", { className: clsx11("p-1.5 rounded-md bg-white border shadow-sm shrink-0", statusConfig.color), children: /* @__PURE__ */ jsx12(StatusIcon, { size: 16, className: clsx11(statusConfig.spin && "animate-spin") }) }),
                /* @__PURE__ */ jsx12("div", { className: "min-w-0", children: /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2 min-w-0", children: [
                  /* @__PURE__ */ jsx12("span", { className: clsx11("font-mono font-semibold text-slate-800 truncate", headerText), children: name }),
                  /* @__PURE__ */ jsx12(
                    "span",
                    {
                      className: clsx11(
                        "text-[10px] px-1.5 py-0.5 rounded-full border bg-white font-medium uppercase tracking-wide",
                        statusConfig.color
                      ),
                      children: status
                    }
                  )
                ] }) })
              ] }),
              /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2 shrink-0 ml-2", children: [
                /* @__PURE__ */ jsx12("span", { className: "pointer-events-none", "aria-hidden": "true" }),
                /* @__PURE__ */ jsx12(
                  "button",
                  {
                    type: "button",
                    onClick: onQuickCopy,
                    className: "pointer-events-auto select-none p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-white/60 transition-colors",
                    title: "Copy formatted text",
                    children: copied ? /* @__PURE__ */ jsx12(Check3, { size: 14, className: "text-emerald-600" }) : /* @__PURE__ */ jsx12(CopyIcon, { size: 14 })
                  }
                ),
                /* @__PURE__ */ jsx12(
                  "button",
                  {
                    type: "button",
                    onClick: onCopyRaw,
                    className: "pointer-events-auto select-none p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-white/60 transition-colors",
                    title: "Copy raw (args + result)",
                    children: /* @__PURE__ */ jsx12(CopyIcon, { size: 14 })
                  }
                ),
                /* @__PURE__ */ jsx12("div", { className: "text-slate-400", children: isExpanded ? /* @__PURE__ */ jsx12(ChevronDown, { size: 16 }) : /* @__PURE__ */ jsx12(ChevronRight, { size: 16 }) })
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsx12(AnimatePresence, { initial: false, children: isExpanded && /* @__PURE__ */ jsx12(
          motion.div,
          {
            initial: { height: 0, opacity: 0 },
            animate: { height: "auto", opacity: 1 },
            exit: { height: 0, opacity: 0 },
            transition: { duration: 0.2 },
            children: /* @__PURE__ */ jsxs11(
              "div",
              {
                ref: contentRef,
                className: clsx11(
                  padding,
                  "pt-0 border-t border-black/5 space-y-3 bg-white/50",
                  "select-text"
                  // critical: allow selection in body
                ),
                children: [
                  /* @__PURE__ */ jsx12(CodeBlock2, { label: "Input Arguments", data: args, colorClass: "text-slate-500" }),
                  result !== void 0 && result !== null && /* @__PURE__ */ jsx12("div", { className: "flex justify-center text-slate-300 py-1 select-none", children: /* @__PURE__ */ jsx12(ArrowRightLeft, { size: 14, className: "rotate-90" }) }),
                  result !== void 0 && result !== null && /* @__PURE__ */ jsx12(SmartResult, { result }),
                  (result === void 0 || result === null) && status === "executing" && /* @__PURE__ */ jsxs11("div", { className: "text-xs text-slate-400 italic py-2 text-center flex items-center justify-center gap-2 select-none", children: [
                    /* @__PURE__ */ jsx12(Loader22, { size: 12, className: "animate-spin" }),
                    "Waiting for tool completion..."
                  ] })
                ]
              }
            )
          }
        ) })
      ]
    }
  ) });
};

// src/renderers/components/blocks/ToolResultBlock.tsx
import { jsx as jsx13, jsxs as jsxs12 } from "react/jsx-runtime";
var ToolResultBlock = ({ block, surface }) => {
  const theme = useChatTheme();
  const { registry } = useRegistry();
  const toolName = block.name || "Tool Output";
  const status = block.status || "success";
  const ThemeRenderer = theme.toolResultRenderers?.[toolName];
  const RegistryRenderer = registry?.toolResultRenderers && registry.toolResultRenderers[toolName] || null;
  const Renderer = ThemeRenderer || RegistryRenderer;
  const copyText2 = useMemo8(() => {
    try {
      if (typeof block.result === "string") return block.result;
      return JSON.stringify(block.result, null, 2);
    } catch {
      return String(block.result);
    }
  }, [block.result]);
  return /* @__PURE__ */ jsx13("div", { className: clsx12(surface === "widget" && "max-w-full"), children: /* @__PURE__ */ jsx13(
    BlockFrame,
    {
      blockId: block.id,
      title: /* @__PURE__ */ jsxs12("div", { className: "flex items-center gap-2 min-w-0", children: [
        /* @__PURE__ */ jsx13(Blocks, { size: 14, className: "text-slate-400 shrink-0" }),
        /* @__PURE__ */ jsx13("span", { className: "truncate", children: toolName })
      ] }),
      subtitle: /* @__PURE__ */ jsx13("span", { className: "text-[11px] text-slate-500", children: status === "error" ? "Tool failed" : "Tool result" }),
      badge: status === "error" ? /* @__PURE__ */ jsxs12("span", { className: "inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700", children: [
        /* @__PURE__ */ jsx13(AlertCircle2, { size: 10 }),
        "error"
      ] }) : /* @__PURE__ */ jsx13("span", { className: "inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700", children: "success" }),
      allowFullscreen: surface !== "widget",
      collapsible: true,
      actions: /* @__PURE__ */ jsx13(
        BlockActions,
        {
          getCopyText: () => copyText2
        }
      ),
      children: Renderer ? /* @__PURE__ */ jsx13("div", { className: "min-w-0", children: /* @__PURE__ */ jsx13(Renderer, { callId: block.callId, name: toolName, result: block.result, status }) }) : /* @__PURE__ */ jsx13(
        ToolBubble,
        {
          toolCallId: block.callId,
          name: toolName,
          args: { _info: "Arguments hidden in tool result block" },
          result: block.result,
          status: status === "error" ? "error" : "success",
          messageId: block.messageId
        }
      )
    }
  ) });
};

// src/renderers/components/blocks/ReasoningBlock.tsx
import { useMemo as useMemo9 } from "react";
import clsx13 from "clsx";
import { BrainCircuit, ChevronDown as ChevronDown2, ChevronRight as ChevronRight2, Lightbulb } from "lucide-react";
import { useMessageUI as useMessageUI4 } from "@dopomogai/chat-react";
import { jsx as jsx14, jsxs as jsxs13 } from "react/jsx-runtime";
var ReasoningBlock = ({ block, surface }) => {
  const { getRendererState, setRendererState } = useMessageUI4(block.messageId);
  const key = `reasoning:${block.id}`;
  const pref = (() => {
    try {
      if (typeof window === "undefined") return null;
      const v = localStorage.getItem("dopomogai:ui:reasoningDefault");
      return v === "show" || v === "hide" ? v : null;
    } catch {
      return null;
    }
  })();
  const defaultExpanded = block.isComplete ? pref === "show" ? true : pref === "hide" ? false : surface === "studio" : true;
  const ui = getRendererState(key, { expanded: defaultExpanded });
  const setExpanded = (v) => setRendererState(key, { expanded: v });
  const title = useMemo9(() => {
    const Icon = block.isComplete ? Lightbulb : BrainCircuit;
    return /* @__PURE__ */ jsxs13("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx14(Icon, { size: 14, className: clsx13(block.isComplete ? "text-slate-400" : "text-amber-500") }),
      /* @__PURE__ */ jsx14("span", { className: "text-xs font-semibold", children: block.isComplete ? "Thought process" : "Reasoning\u2026" })
    ] });
  }, [block.isComplete]);
  const badge = block.isComplete ? /* @__PURE__ */ jsx14("span", { className: "inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600", children: "complete" }) : /* @__PURE__ */ jsx14("span", { className: "inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700", children: "thinking" });
  const copyText2 = block.content || "";
  return /* @__PURE__ */ jsx14(
    BlockFrame,
    {
      blockId: block.id,
      title: /* @__PURE__ */ jsxs13(
        "button",
        {
          type: "button",
          onClick: () => setExpanded(!ui.expanded),
          className: clsx13(
            "flex items-center gap-2 min-w-0",
            "text-left select-none",
            "hover:opacity-90 transition-opacity"
          ),
          title: ui.expanded ? "Collapse reasoning" : "Expand reasoning",
          children: [
            /* @__PURE__ */ jsx14("span", { className: "text-slate-400 shrink-0", children: ui.expanded ? /* @__PURE__ */ jsx14(ChevronDown2, { size: 14 }) : /* @__PURE__ */ jsx14(ChevronRight2, { size: 14 }) }),
            /* @__PURE__ */ jsx14("span", { className: "truncate", children: title })
          ]
        }
      ),
      subtitle: /* @__PURE__ */ jsx14("span", { className: "text-[11px] text-slate-500", children: "Reasoning is optional and may be incomplete." }),
      badge,
      allowFullscreen: surface !== "widget",
      collapsible: false,
      actions: /* @__PURE__ */ jsx14(BlockActions, { getCopyText: () => copyText2 }),
      className: clsx13(
        block.isComplete ? "bg-white" : "bg-amber-50/40",
        block.isComplete ? "border-slate-200" : "border-amber-200"
      ),
      children: ui.expanded ? /* @__PURE__ */ jsx14(
        "div",
        {
          className: clsx13(
            "rounded-lg border p-3",
            block.isComplete ? "border-slate-200 bg-slate-50/30" : "border-amber-200 bg-white/60"
          ),
          children: /* @__PURE__ */ jsxs13("div", { className: "text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap", children: [
            block.content || /* @__PURE__ */ jsx14("span", { className: "text-slate-400 italic", children: "Analyzing\u2026" }),
            !block.isComplete && /* @__PURE__ */ jsx14("span", { className: "inline-block w-1.5 h-3 ml-1 bg-amber-400 animate-pulse align-middle" })
          ] })
        }
      ) : /* @__PURE__ */ jsx14("div", { className: "text-xs text-slate-400 italic", children: "Reasoning hidden\u2026" })
    }
  );
};

// src/renderers/components/attachments/AttachmentGroupBlock.tsx
import { useEffect as useEffect8, useMemo as useMemo14, useState as useState10 } from "react";
import clsx19 from "clsx";
import { Paperclip, Loader2 as Loader26 } from "lucide-react";
import { useChat as useChat2, useChatClient as useChatClient6 } from "@dopomogai/chat-react";

// src/renderers/components/attachments/AttachmentGroupModal.tsx
import { useEffect as useEffect7, useMemo as useMemo13, useRef as useRef5, useState as useState9 } from "react";
import { useChatClient as useChatClient5, useFiles as useFiles2, prefetchFileMetadata as prefetchFileMetadata3, fileMetadataCache as fileMetadataCache2 } from "@dopomogai/chat-react";
import { FilePurposeEnum as FilePurposeEnum2 } from "@dopomogai/types";
import clsx18 from "clsx";
import { Loader2 as Loader25, Plus as Plus2, Trash2 as Trash22, Search as Search3, LayoutGrid as LayoutGrid2, FolderTree as FolderTree2, ArrowUpDown } from "lucide-react";

// src/renderers/components/ConfirmDeleteDialog.tsx
import { useEffect as useEffect3, useState as useState5 } from "react";
import { createPortal as createPortal4 } from "react-dom";
import clsx14 from "clsx";
import { X as X4 } from "lucide-react";
import { jsx as jsx15, jsxs as jsxs14 } from "react/jsx-runtime";
function isSSR() {
  return typeof document === "undefined";
}
var ConfirmDeleteDialog = ({
  isOpen,
  title = "Delete message",
  message = "Are you sure you want to delete this message? This action cannot be undone.",
  confirmText = "Delete",
  cancelText = "Cancel",
  localStorageKey = "dopomogai:chat:skipMessageDeleteConfirm",
  onConfirm,
  onClose
}) => {
  const [dontAsk, setDontAsk] = useState5(false);
  useEffect3(() => {
    if (!isOpen) return;
    setDontAsk(false);
  }, [isOpen]);
  if (!isOpen || isSSR()) return null;
  return createPortal4(
    /* @__PURE__ */ jsxs14("div", { className: "fixed inset-0 z-[9999]", children: [
      /* @__PURE__ */ jsx15(
        "button",
        {
          type: "button",
          onClick: onClose,
          "aria-label": "Close",
          className: "absolute inset-0 bg-slate-900/70 backdrop-blur-sm cursor-default"
        }
      ),
      /* @__PURE__ */ jsx15("div", { className: "absolute inset-0 flex items-center justify-center p-4", children: /* @__PURE__ */ jsxs14("div", { className: "w-full max-w-sm rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden", children: [
        /* @__PURE__ */ jsxs14("div", { className: "flex items-center justify-between px-4 py-3 border-b border-slate-200", children: [
          /* @__PURE__ */ jsx15("div", { className: "text-sm font-semibold text-slate-800", children: title }),
          /* @__PURE__ */ jsx15(
            "button",
            {
              onClick: onClose,
              className: "p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors",
              children: /* @__PURE__ */ jsx15(X4, { size: 16 })
            }
          )
        ] }),
        /* @__PURE__ */ jsx15("div", { className: "px-4 py-3 text-sm text-slate-600", children: message }),
        /* @__PURE__ */ jsxs14("div", { className: "px-4 py-2 flex items-center gap-2", children: [
          /* @__PURE__ */ jsx15(
            "input",
            {
              id: "dont-ask",
              type: "checkbox",
              className: "h-4 w-4 rounded border-slate-300 text-blue-600",
              checked: dontAsk,
              onChange: (e) => setDontAsk(e.target.checked)
            }
          ),
          /* @__PURE__ */ jsx15("label", { htmlFor: "dont-ask", className: "text-[13px] text-slate-600 select-none", children: "Don\u2019t ask again" })
        ] }),
        /* @__PURE__ */ jsxs14("div", { className: "px-4 py-3 border-t border-slate-200 flex justify-end gap-2", children: [
          /* @__PURE__ */ jsx15(
            "button",
            {
              className: "px-3 py-1.5 text-sm rounded-md text-slate-600 hover:bg-slate-100",
              onClick: onClose,
              children: cancelText
            }
          ),
          /* @__PURE__ */ jsx15(
            "button",
            {
              className: clsx14(
                "px-3 py-1.5 text-sm rounded-md text-white",
                "bg-rose-600 hover:bg-rose-700"
              ),
              onClick: () => {
                if (dontAsk) {
                  try {
                    localStorage.setItem(localStorageKey, "true");
                  } catch {
                  }
                }
                onConfirm(dontAsk);
              },
              children: confirmText
            }
          )
        ] })
      ] }) })
    ] }),
    document.body
  );
};
function shouldAskDeleteConfirm(key = "dopomogai:chat:skipMessageDeleteConfirm") {
  try {
    const v = localStorage.getItem(key);
    return v !== "true";
  } catch {
    return true;
  }
}

// src/renderers/components/attachments/KBPickerPanel.tsx
import { useCallback, useEffect as useEffect4, useMemo as useMemo10, useRef as useRef4, useState as useState6 } from "react";
import clsx15 from "clsx";
import { FolderTree, Search, CheckSquare, Square as Square2, FilePlus2, Upload, Save, X as X5, Loader2 as Loader23, Link } from "lucide-react";
import { useChatClient as useChatClient2, useFileMetadata as useFileMetadata2, prefetchFileMetadata, useFiles } from "@dopomogai/chat-react";
import { FilePurposeEnum } from "@dopomogai/types";
import { jsx as jsx16, jsxs as jsxs15 } from "react/jsx-runtime";
var FileRow = ({ node, checked, toggle }) => {
  const { metadata } = useFileMetadata2(node.data, { lazy: false, ttlMs: 3e4 });
  const tokens = metadata?.tokens_estimated ?? null;
  const name = node.label.split("/").pop() || node.label;
  const path = node.label;
  return /* @__PURE__ */ jsxs15("div", { className: "flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-slate-50", children: [
    /* @__PURE__ */ jsx16("button", { className: "shrink-0", onClick: toggle, title: checked ? "Unselect" : "Select", children: checked ? /* @__PURE__ */ jsx16(CheckSquare, { size: 16, className: "text-blue-600" }) : /* @__PURE__ */ jsx16(Square2, { size: 16, className: "text-slate-400" }) }),
    /* @__PURE__ */ jsxs15("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsx16("div", { className: "text-sm font-medium text-slate-800 truncate", children: name }),
      /* @__PURE__ */ jsx16("div", { className: "text-[11px] text-slate-500 font-mono truncate", children: path })
    ] }),
    tokens != null && /* @__PURE__ */ jsxs15("div", { className: "text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5", children: [
      "~",
      Number(tokens).toLocaleString(),
      " tok"
    ] })
  ] });
};
var KBPickerPanel = ({
  onAttach,
  onAttachPaths,
  defaultProjectId,
  multiSelect = true,
  className
}) => {
  const client = useChatClient2();
  const { uploadFile } = useFiles();
  const [projects, setProjects] = useState6([]);
  const [projectId, setProjectId] = useState6(defaultProjectId);
  const [nodes, setNodes] = useState6([]);
  const [query, setQuery] = useState6("");
  const [selected, setSelected] = useState6(/* @__PURE__ */ new Set());
  const [mode, setMode] = useState6("none");
  const [newPath, setNewPath] = useState6("");
  const [newContent, setNewContent] = useState6("");
  const [newSaving, setNewSaving] = useState6(false);
  const fileInputRef = useRef4(null);
  const [uploadPath, setUploadPath] = useState6("");
  const [uploading, setUploading] = useState6(false);
  const [pendingFile, setPendingFile] = useState6(null);
  useEffect4(() => {
    (async () => {
      const list = await client.listKnowledgeProjects();
      setProjects(list || []);
      const first = list?.[0]?.id;
      if (first) setProjectId((prev) => prev || first);
    })();
  }, [client]);
  const refreshTree = useCallback(async () => {
    const tree = await client.getKnowledgeProjectTree(projectId);
    setNodes(Array.isArray(tree) ? tree : []);
    const ids = (tree || []).map((n) => n.data).filter(Boolean);
    if (ids.length) await prefetchFileMetadata(ids);
  }, [client, projectId]);
  useEffect4(() => {
    void refreshTree();
  }, [refreshTree]);
  const filtered = useMemo10(() => {
    const q = query.trim().toLowerCase();
    if (!q) return nodes;
    return nodes.filter((n) => n.label.toLowerCase().includes(q));
  }, [nodes, query]);
  const grouped = useMemo10(() => {
    const map = {};
    for (const n of filtered) {
      const top = (n.label || "").split("/").filter(Boolean)[0] || "(root)";
      (map[top] || (map[top] = [])).push(n);
    }
    return map;
  }, [filtered]);
  const toggle = (fileId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else {
        if (!multiSelect) next.clear();
        next.add(fileId);
      }
      return next;
    });
  };
  const attachSelectedById = () => {
    const ids = Array.from(selected);
    if (ids.length) onAttach(ids);
  };
  const attachSelectedByPath = () => {
    if (!onAttachPaths || !projectId) return;
    if (!nodes || nodes.length === 0) return;
    const items = [];
    const selectedIds = Array.from(selected);
    for (const fid of selectedIds) {
      const node = nodes.find((n) => n.data === fid);
      if (!node) continue;
      const path = node.label;
      const filename = path.split("/").pop() || path;
      items.push({ kb_project_id: projectId, path, filename, media_type: "application/octet-stream" });
    }
    if (items.length) onAttachPaths(items);
  };
  const doSaveNewText = async () => {
    if (!newPath.trim() || !newContent.trim()) return;
    setNewSaving(true);
    try {
      const res = await client.saveFsFile({
        project_id: projectId,
        file_path: newPath.trim(),
        content: newContent,
        commit_message: "FE: create text file"
      });
      if (res && res.head_file_id) {
        setMode("none");
        setNewPath("");
        setNewContent("");
        await refreshTree();
      }
    } finally {
      setNewSaving(false);
    }
  };
  const onPickBinary = () => fileInputRef.current?.click();
  const onBinarySelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    setUploadPath((prev) => prev || f.name);
  };
  const doUploadBinaryToKB = async () => {
    if (!pendingFile || !uploadPath.trim()) return;
    setUploading(true);
    try {
      const uploaded = await uploadFile(pendingFile, FilePurposeEnum.KnowledgeSource);
      if (!uploaded || !uploaded.id) throw new Error("Upload failed");
      const linked = await client.linkUploadedFileToKB({
        project_id: projectId,
        file_path: uploadPath.trim(),
        uploaded_file_id: uploaded.id,
        commit_message: "FE: upload binary to KB"
      });
      if (linked) {
        setMode("none");
        setPendingFile(null);
        setUploadPath("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        await refreshTree();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
    }
  };
  return /* @__PURE__ */ jsxs15("div", { className: clsx15("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs15("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsxs15("div", { className: "relative", children: [
        /* @__PURE__ */ jsx16(Search, { size: 14, className: "absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" }),
        /* @__PURE__ */ jsx16(
          "input",
          {
            className: "pl-7 pr-3 py-1.5 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20",
            placeholder: "Search path\u2026",
            value: query,
            onChange: (e) => setQuery(e.target.value)
          }
        )
      ] }),
      /* @__PURE__ */ jsxs15("div", { className: "flex items-center gap-2 ml-auto", children: [
        /* @__PURE__ */ jsx16(FolderTree, { size: 14, className: "text-slate-400" }),
        /* @__PURE__ */ jsx16(
          "select",
          {
            className: "text-sm border border-slate-200 rounded-md py-1.5 px-2",
            value: projectId,
            onChange: (e) => setProjectId(e.target.value),
            children: projects.map((p) => /* @__PURE__ */ jsx16("option", { value: p.id, children: p.name }, p.id))
          }
        ),
        /* @__PURE__ */ jsxs15("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsxs15(
            "button",
            {
              onClick: () => setMode(mode === "new-text" ? "none" : "new-text"),
              className: clsx15(
                "px-2.5 py-1.5 text-sm rounded-md border",
                mode === "new-text" ? "bg-blue-600 text-white border-blue-600" : "text-slate-700 hover:bg-slate-50 border-slate-200"
              ),
              title: "New KB text file",
              children: [
                /* @__PURE__ */ jsx16(FilePlus2, { size: 14 }),
                " New"
              ]
            }
          ),
          /* @__PURE__ */ jsxs15(
            "button",
            {
              onClick: () => setMode(mode === "upload-binary" ? "none" : "upload-binary"),
              className: clsx15(
                "px-2.5 py-1.5 text-sm rounded-md border",
                mode === "upload-binary" ? "bg-blue-600 text-white border-blue-600" : "text-slate-700 hover:bg-slate-50 border-slate-200"
              ),
              title: "Upload binary to KB",
              children: [
                /* @__PURE__ */ jsx16(Upload, { size: 14 }),
                " Upload"
              ]
            }
          ),
          /* @__PURE__ */ jsx16(
            "button",
            {
              onClick: attachSelectedById,
              disabled: selected.size === 0,
              className: clsx15(
                "px-2.5 py-1.5 text-sm rounded-md",
                selected.size > 0 ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-slate-200 text-slate-500 cursor-not-allowed"
              ),
              title: "Attach by immutable file id",
              children: "Attach"
            }
          ),
          /* @__PURE__ */ jsxs15(
            "button",
            {
              onClick: attachSelectedByPath,
              disabled: selected.size === 0 || !onAttachPaths,
              className: clsx15(
                "px-2.5 py-1.5 text-sm rounded-md flex items-center gap-1",
                selected.size > 0 && onAttachPaths ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-200 text-slate-500 cursor-not-allowed"
              ),
              title: "Attach by path (HEAD)",
              children: [
                /* @__PURE__ */ jsx16(Link, { size: 14 }),
                " Attach by path"
              ]
            }
          )
        ] })
      ] })
    ] }),
    mode === "new-text" && /* @__PURE__ */ jsxs15("div", { className: "rounded-md border border-slate-200 p-2 space-y-2", children: [
      /* @__PURE__ */ jsxs15("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx16(
          "input",
          {
            value: newPath,
            onChange: (e) => setNewPath(e.target.value),
            placeholder: "docs/intro.md",
            className: "flex-1 px-3 py-1.5 rounded-md border border-slate-200 text-sm"
          }
        ),
        /* @__PURE__ */ jsxs15(
          "button",
          {
            onClick: doSaveNewText,
            disabled: !newPath.trim() || !newContent.trim() || newSaving,
            className: clsx15(
              "px-2.5 py-1.5 text-sm rounded-md",
              newPath.trim() && newContent.trim() && !newSaving ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-200 text-slate-500 cursor-not-allowed"
            ),
            children: [
              newSaving ? /* @__PURE__ */ jsx16(Loader23, { size: 14, className: "animate-spin" }) : /* @__PURE__ */ jsx16(Save, { size: 14 }),
              " Save"
            ]
          }
        ),
        /* @__PURE__ */ jsxs15(
          "button",
          {
            onClick: () => setMode("none"),
            className: "px-2 py-1.5 text-sm rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50",
            children: [
              /* @__PURE__ */ jsx16(X5, { size: 14 }),
              " Cancel"
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsx16(
        "textarea",
        {
          value: newContent,
          onChange: (e) => setNewContent(e.target.value),
          rows: 6,
          placeholder: "# New doc\\nWrite your markdown or code\u2026",
          className: "w-full text-sm rounded-md border border-slate-200 p-2 font-mono"
        }
      )
    ] }),
    mode === "upload-binary" && /* @__PURE__ */ jsx16("div", { className: "rounded-md border border-slate-200 p-2 space-y-2", children: /* @__PURE__ */ jsxs15("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx16(
        "input",
        {
          type: "file",
          ref: fileInputRef,
          onChange: onBinarySelected,
          className: "text-sm"
        }
      ),
      /* @__PURE__ */ jsx16(
        "input",
        {
          value: uploadPath,
          onChange: (e) => setUploadPath(e.target.value),
          placeholder: "assets/logo.png",
          className: "flex-1 px-3 py-1.5 rounded-md border border-slate-200 text-sm"
        }
      ),
      /* @__PURE__ */ jsxs15(
        "button",
        {
          onClick: doUploadBinaryToKB,
          disabled: !pendingFile || !uploadPath.trim() || uploading,
          className: clsx15(
            "px-2.5 py-1.5 text-sm rounded-md",
            pendingFile && uploadPath.trim() && !uploading ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-200 text-slate-500 cursor-not-allowed"
          ),
          children: [
            uploading ? /* @__PURE__ */ jsx16(Loader23, { size: 14, className: "animate-spin" }) : /* @__PURE__ */ jsx16(Upload, { size: 14 }),
            " Upload"
          ]
        }
      ),
      /* @__PURE__ */ jsxs15(
        "button",
        {
          onClick: () => {
            setMode("none");
            setPendingFile(null);
            setUploadPath("");
            if (fileInputRef.current) fileInputRef.current.value = "";
          },
          className: "px-2 py-1.5 text-sm rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50",
          children: [
            /* @__PURE__ */ jsx16(X5, { size: 14 }),
            " Cancel"
          ]
        }
      )
    ] }) }),
    /* @__PURE__ */ jsxs15("div", { className: "space-y-3 min-h-[180px] max-h-[50vh] overflow-auto custom-scrollbar", children: [
      Object.entries(grouped).map(([dir, list]) => /* @__PURE__ */ jsxs15("div", { children: [
        /* @__PURE__ */ jsx16("div", { className: "text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1", children: dir }),
        /* @__PURE__ */ jsx16("div", { className: "space-y-1", children: list.map((n) => /* @__PURE__ */ jsx16("div", { className: "flex items-center", children: /* @__PURE__ */ jsx16(FileRow, { node: n, checked: selected.has(n.data), toggle: () => toggle(n.data) }) }, n.key)) })
      ] }, dir)),
      filtered.length === 0 && /* @__PURE__ */ jsx16("div", { className: "text-[12px] text-slate-400 italic px-2", children: "No files found." })
    ] })
  ] });
};

// src/renderers/components/attachments/GroupPickerPanel.tsx
import { useEffect as useEffect5, useMemo as useMemo11, useState as useState7 } from "react";
import clsx16 from "clsx";
import { CheckSquare as CheckSquare2, Folder, Layers, Loader2 as Loader24, Square as Square3 } from "lucide-react";
import { prefetchFileMetadata as prefetchFileMetadata2, fileMetadataCache, useChatClient as useChatClient3 } from "@dopomogai/chat-react";
import { jsx as jsx17, jsxs as jsxs16 } from "react/jsx-runtime";
var GroupPickerPanel = ({ onAttach, className }) => {
  const client = useChatClient3();
  const [groups, setGroups] = useState7([]);
  const [loadingGroups, setLoadingGroups] = useState7(false);
  const [activeGroupId, setActiveGroupId] = useState7(null);
  const [items, setItems] = useState7([]);
  const [loadingItems, setLoadingItems] = useState7(false);
  const [selected, setSelected] = useState7(/* @__PURE__ */ new Set());
  useEffect5(() => {
    let cancelled = false;
    const run = async () => {
      setLoadingGroups(true);
      try {
        const res = await client.listFileGroups();
        if (!cancelled && Array.isArray(res)) setGroups(res);
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [client]);
  useEffect5(() => {
    if (!activeGroupId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoadingItems(true);
      try {
        const list = await client.getFileGroupItems(activeGroupId);
        if (!cancelled && Array.isArray(list)) {
          setItems(list);
          const fileIds = list.filter((it) => it.kind === "file" && it.file_id).map((it) => it.file_id);
          if (fileIds.length) await prefetchFileMetadata2(fileIds);
        }
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [activeGroupId, client]);
  const attachableFileIds = useMemo11(() => {
    return items.filter((it) => it.kind === "file" && it.file_id).map((it) => it.file_id);
  }, [items]);
  const tokensTotal = useMemo11(() => {
    let sum = 0;
    for (const fid of attachableFileIds) {
      const meta = fileMetadataCache.get(fid);
      const t = meta?.tokens_estimated ?? 0;
      sum += Number(t) || 0;
    }
    return sum;
  }, [attachableFileIds, items]);
  const toggle = (fid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fid)) next.delete(fid);
      else next.add(fid);
      return next;
    });
  };
  const visibleFileIds = useMemo11(() => {
    if (!activeGroupId) return [];
    return attachableFileIds;
  }, [activeGroupId, attachableFileIds]);
  return /* @__PURE__ */ jsxs16("div", { className: clsx16("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs16("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx17(Layers, { size: 16, className: "text-slate-500" }),
      /* @__PURE__ */ jsx17("div", { className: "text-sm font-semibold text-slate-800", children: "Groups" })
    ] }),
    /* @__PURE__ */ jsxs16("div", { className: "flex gap-3", children: [
      /* @__PURE__ */ jsxs16("div", { className: "w-56 shrink-0 border border-slate-200 rounded-md overflow-hidden", children: [
        /* @__PURE__ */ jsx17("div", { className: "px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200", children: "All groups" }),
        /* @__PURE__ */ jsxs16("div", { className: "max-h-[40vh] overflow-auto custom-scrollbar", children: [
          loadingGroups && /* @__PURE__ */ jsx17("div", { className: "p-3 text-xs text-slate-400", children: "Loading\u2026" }),
          !loadingGroups && groups.length === 0 && /* @__PURE__ */ jsx17("div", { className: "p-3 text-xs text-slate-400", children: "No groups yet." }),
          groups.map((g) => /* @__PURE__ */ jsxs16(
            "button",
            {
              onClick: () => {
                setActiveGroupId(g.id);
                setSelected(/* @__PURE__ */ new Set());
              },
              className: clsx16(
                "w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-slate-50",
                activeGroupId === g.id ? "bg-blue-50 text-blue-700" : "text-slate-700"
              ),
              children: [
                /* @__PURE__ */ jsx17(Folder, { size: 14 }),
                /* @__PURE__ */ jsx17("span", { className: "truncate", children: g.name })
              ]
            },
            g.id
          ))
        ] })
      ] }),
      /* @__PURE__ */ jsxs16("div", { className: "flex-1 border border-slate-200 rounded-md overflow-hidden", children: [
        /* @__PURE__ */ jsxs16("div", { className: "px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200 flex items-center justify-between", children: [
          /* @__PURE__ */ jsx17("span", { children: "Group items" }),
          /* @__PURE__ */ jsxs16("span", { className: "text-[11px] text-slate-500", children: [
            loadingItems ? "Loading\u2026" : `${visibleFileIds.length} file(s)`,
            " \u2022 Tokens: ~",
            tokensTotal.toLocaleString()
          ] })
        ] }),
        /* @__PURE__ */ jsxs16("div", { className: "max-h-[40vh] overflow-auto custom-scrollbar", children: [
          !activeGroupId && /* @__PURE__ */ jsx17("div", { className: "p-3 text-xs text-slate-400", children: "Select a group" }),
          activeGroupId && loadingItems && /* @__PURE__ */ jsxs16("div", { className: "p-3 text-xs text-slate-400 flex items-center gap-2", children: [
            /* @__PURE__ */ jsx17(Loader24, { size: 14, className: "animate-spin" }),
            " Loading files\u2026"
          ] }),
          activeGroupId && !loadingItems && visibleFileIds.length === 0 && /* @__PURE__ */ jsx17("div", { className: "p-3 text-xs text-slate-400", children: "No files in this group." }),
          activeGroupId && !loadingItems && visibleFileIds.map((fid) => {
            const meta = fileMetadataCache.get(fid);
            const name = meta?.filename || `file-${fid.slice(0, 6)}`;
            const tokens = meta?.tokens_estimated ?? null;
            const checked = selected.has(fid);
            return /* @__PURE__ */ jsxs16("div", { className: "flex items-center gap-3 px-3 py-2 border-b last:border-b-0 hover:bg-slate-50", children: [
              /* @__PURE__ */ jsx17("button", { onClick: () => toggle(fid), className: "shrink-0", title: checked ? "Unselect" : "Select", children: checked ? /* @__PURE__ */ jsx17(CheckSquare2, { size: 16, className: "text-blue-600" }) : /* @__PURE__ */ jsx17(Square3, { size: 16, className: "text-slate-400" }) }),
              /* @__PURE__ */ jsxs16("div", { className: "min-w-0 flex-1", children: [
                /* @__PURE__ */ jsx17("div", { className: "text-sm text-slate-800 truncate", children: name }),
                /* @__PURE__ */ jsx17("div", { className: "text-[11px] text-slate-500 truncate", children: fid })
              ] }),
              tokens != null && /* @__PURE__ */ jsxs16("div", { className: "text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5", children: [
                "~",
                Number(tokens).toLocaleString(),
                " t"
              ] })
            ] }, fid);
          })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx17("div", { className: "flex items-center justify-end gap-2", children: /* @__PURE__ */ jsxs16(
      "button",
      {
        onClick: async () => {
          const ids = selected.size > 0 ? Array.from(selected) : visibleFileIds;
          if (ids.length) await onAttach(ids);
          setSelected(/* @__PURE__ */ new Set());
        },
        disabled: !activeGroupId || selected.size === 0 && visibleFileIds.length === 0,
        className: clsx16(
          "px-3 py-1.5 rounded-md text-sm",
          !activeGroupId ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"
        ),
        children: [
          "Attach ",
          selected.size ? `(${selected.size})` : ""
        ]
      }
    ) })
  ] });
};

// src/renderers/components/attachments/LibraryPanel.tsx
import { useEffect as useEffect6, useMemo as useMemo12, useState as useState8 } from "react";
import clsx17 from "clsx";
import { CheckSquare as CheckSquare3, Search as Search2, Square as Square4 } from "lucide-react";
import { useChatClient as useChatClient4 } from "@dopomogai/chat-react";
import { jsx as jsx18, jsxs as jsxs17 } from "react/jsx-runtime";
var LibraryPanel = ({ onAttach, className }) => {
  const client = useChatClient4();
  const [query, setQuery] = useState8("");
  const [items, setItems] = useState8([]);
  const [loading, setLoading] = useState8(false);
  const [selected, setSelected] = useState8(/* @__PURE__ */ new Set());
  useEffect6(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await client.listLibraryFiles({ q: query, limit: 200, sort: "newest" });
        if (!cancelled && res && Array.isArray(res.items)) {
          setItems(res.items);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [client, query]);
  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const tokensTotal = useMemo12(() => {
    let sum = 0;
    for (const it of items) {
      if (selected.has(it.id)) {
        const t = it.tokens_estimated ?? 0;
        sum += Number(t) || 0;
      }
    }
    return sum;
  }, [items, selected]);
  return /* @__PURE__ */ jsxs17("div", { className: clsx17("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs17("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsxs17("div", { className: "relative flex-1", children: [
        /* @__PURE__ */ jsx18(Search2, { size: 14, className: "absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" }),
        /* @__PURE__ */ jsx18(
          "input",
          {
            value: query,
            onChange: (e) => setQuery(e.target.value),
            placeholder: "Search files\u2026",
            className: "w-full pl-7 pr-3 py-1.5 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          }
        )
      ] }),
      /* @__PURE__ */ jsx18("div", { className: "text-[11px] text-slate-500", children: loading ? "Loading\u2026" : `${items.length} items` })
    ] }),
    /* @__PURE__ */ jsxs17("div", { className: "min-h-[160px] max-h-[50vh] overflow-auto custom-scrollbar border border-slate-200 rounded-md", children: [
      items.map((it) => {
        const item = it;
        const checked = selected.has(it.id);
        const tokens = item.tokens_estimated ?? null;
        return /* @__PURE__ */ jsxs17("div", { className: "flex items-center gap-3 px-3 py-2 border-b last:border-b-0 hover:bg-slate-50", children: [
          /* @__PURE__ */ jsx18("button", { onClick: () => toggle(it.id), className: "shrink-0", title: checked ? "Unselect" : "Select", children: checked ? /* @__PURE__ */ jsx18(CheckSquare3, { size: 16, className: "text-blue-600" }) : /* @__PURE__ */ jsx18(Square4, { size: 16, className: "text-slate-400" }) }),
          /* @__PURE__ */ jsxs17("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsx18("div", { className: "text-sm text-slate-800 truncate", children: it.filename }),
            /* @__PURE__ */ jsx18("div", { className: "text-[11px] text-slate-500 truncate", children: item.appwrite_metadata?.logical_path || it.filename })
          ] }),
          tokens != null && /* @__PURE__ */ jsxs17("div", { className: "text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5", children: [
            "~",
            Number(tokens).toLocaleString(),
            " t"
          ] })
        ] }, it.id);
      }),
      !loading && items.length === 0 && /* @__PURE__ */ jsx18("div", { className: "p-6 text-center text-sm text-slate-400", children: "No files found." })
    ] }),
    /* @__PURE__ */ jsxs17("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs17("div", { className: "text-[11px] text-slate-500", children: [
        "Selected: ",
        selected.size,
        " \u2022 Tokens: ~",
        tokensTotal.toLocaleString()
      ] }),
      /* @__PURE__ */ jsxs17(
        "button",
        {
          onClick: async () => {
            const ids = Array.from(selected);
            if (ids.length) await onAttach(ids);
            setSelected(/* @__PURE__ */ new Set());
          },
          disabled: selected.size === 0,
          className: clsx17(
            "px-3 py-1.5 rounded-md text-sm",
            selected.size > 0 ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-slate-200 text-slate-500 cursor-not-allowed"
          ),
          children: [
            "Attach ",
            selected.size ? `(${selected.size})` : ""
          ]
        }
      )
    ] })
  ] });
};

// src/renderers/components/attachments/AttachmentGroupModal.tsx
import { Fragment as Fragment5, jsx as jsx19, jsxs as jsxs18 } from "react/jsx-runtime";
var AttachmentGroupModal = ({
  isOpen,
  onClose,
  conversationId,
  messageId,
  files,
  onChanged
}) => {
  const client = useChatClient5();
  const fileInputRef = useRef5(null);
  const [deletingAll, setDeletingAll] = useState9(false);
  const [confirmOpen, setConfirmOpen] = useState9(false);
  const [tab, setTab] = useState9("uploads");
  const [view, setView] = useState9("grid");
  const [query, setQuery] = useState9("");
  const [sortKey, setSortKey] = useState9("newest");
  const [recentIds, setRecentIds] = useState9(/* @__PURE__ */ new Set());
  const { uploadFile, isUploading, activeUploadsCount } = useFiles2({
    onUploadSuccess: async (uploaded) => {
      try {
        await client.addFilesToMessage(conversationId, messageId, [{ fileId: uploaded.id, media_type: uploaded.content_type || void 0 }]);
        setRecentIds((prev) => {
          const next = new Set(prev);
          next.add(uploaded.id);
          return next;
        });
        onChanged?.();
        setTimeout(() => {
          setRecentIds((prev) => {
            const next = new Set(prev);
            next.delete(uploaded.id);
            return next;
          });
        }, 45e3);
      } catch (e) {
        console.error("Attach failed", e);
      }
    }
  });
  const onPickFiles = () => fileInputRef.current?.click();
  const onFileSelect = async (e) => {
    const list = e.target.files ? Array.from(e.target.files) : [];
    for (const f of list) {
      await uploadFile(f, FilePurposeEnum2.AgentInput);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const onDeleteAllClick = () => {
    if (shouldAskDeleteConfirm()) setConfirmOpen(true);
    else void doDeleteAll();
  };
  const doDeleteAll = async () => {
    setDeletingAll(true);
    try {
      await client.removeAllFilesFromMessage(conversationId, messageId);
      onChanged?.();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingAll(false);
    }
  };
  useEffect7(() => {
    const ids = files.map((f) => f.fileId).filter(Boolean);
    if (ids.length) void prefetchFileMetadata3(ids);
  }, [files]);
  const filtered = useMemo13(() => {
    const q = query.trim().toLowerCase();
    const base = q ? files.filter((f) => (f.name || "").toLowerCase().includes(q)) : files.slice();
    const score = (f) => {
      const meta = f.fileId ? fileMetadataCache2.get(f.fileId) : void 0;
      switch (sortKey) {
        case "newest":
        case "oldest": {
          const ts = +new Date(meta?.created_at || meta?.uploaded_at || 0);
          return ts || 0;
        }
        case "name":
          return 0;
        case "type":
          return 0;
        case "tokens":
          return Number(meta?.tokens_estimated ?? 0);
      }
    };
    base.sort((a, b) => {
      if (sortKey === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortKey === "type") return (a.mimeType || "").localeCompare(b.mimeType || "");
      if (sortKey === "tokens") return score(b) - score(a) || (a.name || "").localeCompare(b.name || "");
      const delta = score(b) - score(a);
      return sortKey === "newest" ? delta : -delta;
    });
    return base;
  }, [files, query, sortKey]);
  const treeGroups = useMemo13(() => {
    const groups = {};
    for (const f of filtered) {
      const meta = f.fileId ? fileMetadataCache2.get(f.fileId) : void 0;
      const raw = meta?.appwrite_metadata?.logical_path || meta?.file_path || meta?.path || f.name || "";
      const first = String(raw).split("/").filter(Boolean)[0] || "(root)";
      (groups[first] || (groups[first] = [])).push(f);
    }
    return groups;
  }, [filtered]);
  const [isCreatingFromDrag, setIsCreatingFromDrag] = useState9(false);
  const [newGroupName, setNewGroupName] = useState9("");
  return /* @__PURE__ */ jsxs18(
    FullscreenModal,
    {
      isOpen,
      onClose,
      title: /* @__PURE__ */ jsx19("div", { className: "flex items-center gap-2", children: "Manage files" }),
      contentClassName: "bg-white",
      children: [
        /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-2 mb-3 border-b border-slate-200", children: [
          /* @__PURE__ */ jsx19(
            "button",
            {
              className: clsx18("px-3 py-2 text-sm", tab === "uploads" ? "border-b-2 border-blue-600 text-blue-600" : "text-slate-500"),
              onClick: () => setTab("uploads"),
              children: "Uploads"
            }
          ),
          /* @__PURE__ */ jsx19(
            "button",
            {
              className: clsx18("px-3 py-2 text-sm", tab === "kb" ? "border-b-2 border-blue-600 text-blue-600" : "text-slate-500"),
              onClick: () => setTab("kb"),
              children: "KB"
            }
          ),
          /* @__PURE__ */ jsx19(
            "button",
            {
              className: clsx18("px-3 py-2 text-sm", tab === "library" ? "border-b-2 border-blue-600 text-blue-600" : "text-slate-500"),
              onClick: () => setTab("library"),
              children: "Library"
            }
          ),
          /* @__PURE__ */ jsx19(
            "button",
            {
              className: clsx18("px-3 py-2 text-sm", tab === "groups" ? "border-b-2 border-blue-600 text-blue-600" : "text-slate-500"),
              onClick: () => setTab("groups"),
              children: "Groups"
            }
          ),
          /* @__PURE__ */ jsx19("div", { className: "ml-auto" })
        ] }),
        tab === "uploads" && /* @__PURE__ */ jsxs18(Fragment5, { children: [
          /* @__PURE__ */ jsxs18("div", { className: "flex flex-wrap items-center gap-2 justify-between mb-3", children: [
            /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx19(
                "button",
                {
                  className: clsx18(
                    "px-2.5 py-1.5 text-sm rounded-md border",
                    "text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-slate-200"
                  ),
                  onClick: () => fileInputRef.current?.click(),
                  title: "Add files",
                  children: /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-1", children: [
                    /* @__PURE__ */ jsx19(Plus2, { size: 14 }),
                    " Add files"
                  ] })
                }
              ),
              /* @__PURE__ */ jsx19(
                "button",
                {
                  className: clsx18(
                    "px-2.5 py-1.5 text-sm rounded-md border",
                    "text-rose-600 hover:text-white hover:bg-rose-600 border-rose-200"
                  ),
                  onClick: () => {
                    if (shouldAskDeleteConfirm()) setConfirmOpen(true);
                    else void doDeleteAll();
                  },
                  disabled: deletingAll,
                  title: "Remove all attachments from this message",
                  children: /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-1", children: [
                    deletingAll ? /* @__PURE__ */ jsx19(Loader25, { size: 14, className: "animate-spin" }) : /* @__PURE__ */ jsx19(Trash22, { size: 14 }),
                    "Delete all"
                  ] })
                }
              ),
              /* @__PURE__ */ jsx19(
                "input",
                {
                  ref: fileInputRef,
                  type: "file",
                  className: "hidden",
                  multiple: true,
                  onChange: onFileSelect
                }
              )
            ] }),
            /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsxs18("div", { className: "relative", children: [
                /* @__PURE__ */ jsx19(Search3, { size: 14, className: "absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" }),
                /* @__PURE__ */ jsx19(
                  "input",
                  {
                    type: "text",
                    placeholder: "Search filename\u2026",
                    value: query,
                    onChange: (e) => setQuery(e.target.value),
                    className: "pl-7 pr-3 py-1.5 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-1", children: [
                /* @__PURE__ */ jsx19(ArrowUpDown, { size: 14, className: "text-slate-400" }),
                /* @__PURE__ */ jsxs18(
                  "select",
                  {
                    value: sortKey,
                    onChange: (e) => setSortKey(e.target.value),
                    className: "text-sm border border-slate-200 rounded-md py-1.5 px-2",
                    title: "Sort",
                    children: [
                      /* @__PURE__ */ jsx19("option", { value: "newest", children: "Newest" }),
                      /* @__PURE__ */ jsx19("option", { value: "oldest", children: "Oldest" }),
                      /* @__PURE__ */ jsx19("option", { value: "name", children: "Name" }),
                      /* @__PURE__ */ jsx19("option", { value: "type", children: "Type" }),
                      /* @__PURE__ */ jsx19("option", { value: "tokens", children: "Tokens" })
                    ]
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-1 ml-2", children: [
                /* @__PURE__ */ jsx19(
                  "button",
                  {
                    onClick: () => setView("grid"),
                    className: clsx18("p-1.5 rounded-md border", view === "grid" ? "border-blue-300 text-blue-600 bg-blue-50" : "border-slate-200 text-slate-500"),
                    title: "Grid view",
                    children: /* @__PURE__ */ jsx19(LayoutGrid2, { size: 16 })
                  }
                ),
                /* @__PURE__ */ jsx19(
                  "button",
                  {
                    onClick: () => setView("tree"),
                    className: clsx18("p-1.5 rounded-md border", view === "tree" ? "border-blue-300 text-blue-600 bg-blue-50" : "border-slate-200 text-slate-500"),
                    title: "Filesystem view",
                    children: /* @__PURE__ */ jsx19(FolderTree2, { size: 16 })
                  }
                )
              ] })
            ] })
          ] }),
          view === "grid" ? /* @__PURE__ */ jsx19("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3", children: files.map((f, i) => {
            const isNew = f.fileId && false;
            return /* @__PURE__ */ jsx19(
              "div",
              {
                className: clsx18(isNew && "ring-2 ring-emerald-400/60 rounded-3xl"),
                children: /* @__PURE__ */ jsx19(
                  FileBubble,
                  {
                    url: f.url,
                    name: f.name,
                    type: f.type,
                    mimeType: f.mimeType,
                    fileId: f.fileId,
                    messageId
                  }
                )
              },
              `${f.fileId || f.url}-${i}`
            );
          }) }) : /* @__PURE__ */ jsx19("div", { className: "space-y-4", children: /* @__PURE__ */ jsx19("div", { className: "text-[12px] text-slate-500 italic px-2", children: "Switch to KB tab to browse KB filesystem." }) })
        ] }),
        tab === "kb" && /* @__PURE__ */ jsx19("div", { className: "p-2", children: /* @__PURE__ */ jsx19(
          KBPickerPanel,
          {
            onAttach: async (ids) => {
              await client.addFilesToMessage(conversationId, messageId, ids.map((fid) => ({ file_id: fid })));
              onChanged?.();
            }
          }
        ) }),
        tab === "library" && /* @__PURE__ */ jsx19("div", { className: "p-2", children: /* @__PURE__ */ jsx19(
          LibraryPanel,
          {
            onAttach: async (ids) => {
              await client.addFilesToMessage(conversationId, messageId, ids.map((fid) => ({ file_id: fid })));
              onChanged?.();
            }
          }
        ) }),
        tab === "groups" && /* @__PURE__ */ jsx19("div", { className: "p-2", children: /* @__PURE__ */ jsx19(
          GroupPickerPanel,
          {
            onAttach: async (ids) => {
              await client.addFilesToMessage(conversationId, messageId, ids.map((fid) => ({ file_id: fid })));
              onChanged?.();
            }
          }
        ) }),
        /* @__PURE__ */ jsx19(
          ConfirmDeleteDialog,
          {
            isOpen: confirmOpen,
            onClose: () => setConfirmOpen(false),
            onConfirm: (skip) => {
              if (skip) {
                try {
                  localStorage.setItem("dopomogai:chat:skipMessageDeleteConfirm", "true");
                } catch {
                }
              }
              setConfirmOpen(false);
              void doDeleteAll();
            },
            title: "Remove all attachments",
            message: "Are you sure you want to remove all attachments from this message?",
            confirmText: "Remove all",
            localStorageKey: "dopomogai:chat:skipMessageDeleteConfirm"
          }
        )
      ]
    }
  );
};

// src/renderers/components/attachments/AttachmentGroupBlock.tsx
import { Fragment as Fragment6, jsx as jsx20, jsxs as jsxs19 } from "react/jsx-runtime";
var ATTACHMENTS_SUMMARY_TTL_MS = 60 * 1e3;
var attachmentsSummaryCache = /* @__PURE__ */ new Map();
var attachmentsSummaryInflight = /* @__PURE__ */ new Map();
var isApiError = (value) => {
  return !!value && typeof value === "object" && value.isError === true;
};
async function getAttachmentsSummary(client, conversationId) {
  const cached = attachmentsSummaryCache.get(conversationId);
  if (cached && Date.now() - cached.ts < ATTACHMENTS_SUMMARY_TTL_MS) return cached.data;
  const inflight = attachmentsSummaryInflight.get(conversationId);
  if (inflight) return inflight;
  const promise = (async () => {
    const res = await client.getAttachmentsSummary(conversationId);
    if (isApiError(res)) return [];
    const list = Array.isArray(res) ? res : [];
    attachmentsSummaryCache.set(conversationId, { ts: Date.now(), data: list });
    return list;
  })();
  attachmentsSummaryInflight.set(conversationId, promise);
  try {
    return await promise;
  } finally {
    attachmentsSummaryInflight.delete(conversationId);
  }
}
var AttachmentGroupBlock = ({ block, surface }) => {
  const client = useChatClient6();
  const { activeId, messages } = useChat2();
  const [open, setOpen] = useState10(false);
  const [loadingSummary, setLoadingSummary] = useState10(false);
  const [summary, setSummary] = useState10(null);
  const [isCollapsed, setIsCollapsed] = useState10(false);
  const files = block.files || [];
  useEffect8(() => {
    setSummary(null);
  }, [activeId, block.messageId]);
  const messageIndex = useMemo14(() => {
    if (!block.messageId) return -1;
    return messages.findIndex((m) => m.id === block.messageId);
  }, [messages, block.messageId]);
  useEffect8(() => {
    const cid = activeId || "";
    if (!cid) return;
    if (messageIndex < 0) return;
    if (isCollapsed) return;
    if (summary?.message_index === messageIndex) return;
    let cancelled = false;
    const run = async () => {
      try {
        setLoadingSummary(true);
        const arr = await getAttachmentsSummary(client, cid);
        if (cancelled) return;
        const row = arr.find((it) => it.message_index === messageIndex) || null;
        if (!cancelled) setSummary(row);
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [client, activeId, messageIndex, isCollapsed, summary]);
  const groupTokensText = useMemo14(() => {
    const t = summary?.group_tokens_estimated;
    if (typeof t === "number" && isFinite(t)) return `~${Math.round(t).toLocaleString()} t`;
    return null;
  }, [summary]);
  const title = useMemo14(() => {
    return /* @__PURE__ */ jsxs19("div", { className: "flex items-center gap-2 min-w-0", children: [
      /* @__PURE__ */ jsx20(Paperclip, { size: 14, className: "text-slate-400 shrink-0" }),
      /* @__PURE__ */ jsx20("span", { className: "truncate", children: "Attachments" })
    ] });
  }, []);
  const subtitle = useMemo14(() => {
    return /* @__PURE__ */ jsxs19("span", { className: "text-[11px] text-slate-500 flex items-center gap-2", children: [
      files.length.toLocaleString(),
      " file",
      files.length === 1 ? "" : "s",
      loadingSummary && /* @__PURE__ */ jsx20(Loader26, { size: 12, className: "animate-spin text-slate-300" }),
      !loadingSummary && groupTokensText && /* @__PURE__ */ jsxs19("span", { className: "text-slate-400", children: [
        "\u2022 ",
        groupTokensText
      ] })
    ] });
  }, [files.length, loadingSummary, groupTokensText]);
  return /* @__PURE__ */ jsxs19(Fragment6, { children: [
    /* @__PURE__ */ jsx20(
      BlockFrame,
      {
        blockId: block.id,
        title,
        subtitle,
        badge: /* @__PURE__ */ jsx20("span", { className: "inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 select-none", children: "files" }),
        allowFullscreen: false,
        collapsible: true,
        onCollapseChange: setIsCollapsed,
        actions: /* @__PURE__ */ jsx20("div", { className: "flex items-center gap-1", children: /* @__PURE__ */ jsx20(
          "button",
          {
            type: "button",
            onClick: () => setOpen(true),
            className: "p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors pointer-events-auto",
            title: "Manage files",
            children: "Manage"
          }
        ) }),
        className: clsx19(surface === "widget" && "rounded-lg"),
        bodyClassName: "p-3",
        children: /* @__PURE__ */ jsx20("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3", children: files.map((f, i) => /* @__PURE__ */ jsx20(
          FileBubble,
          {
            url: f.url,
            name: f.name,
            type: f.type,
            mimeType: f.mimeType,
            fileId: f.fileId,
            messageId: block.messageId
          },
          `${f.fileId || f.url}-${i}`
        )) })
      }
    ),
    open && /* @__PURE__ */ jsx20(
      AttachmentGroupModal,
      {
        isOpen: open,
        onClose: () => setOpen(false),
        conversationId: activeId || "",
        messageId: block.messageId,
        files,
        onChanged: () => {
          const cid = activeId;
          if (cid) client.conversationManager.loadConversation(cid);
        }
      }
    )
  ] });
};

// src/renderers/components/MessageBody.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var MessageBody = ({
  messageId,
  role,
  parts,
  content,
  isStreaming,
  surface = "app",
  blockRenderers,
  className
}) => {
  const containerRef = useRef6(null);
  const normalized = useMemo15(() => {
    return normalizeMessage({ messageId, role, parts, content, isStreaming, surface });
  }, [messageId, role, parts, content, isStreaming, surface]);
  const registry = useMemo15(() => {
    const defaults = {
      markdown: MarkdownBlock,
      artifact: ArtifactBlock,
      ui: UIBlock,
      tool_call: ToolCallBlock,
      tool_result: ToolResultBlock,
      reasoning: ReasoningBlock,
      attachments: AttachmentGroupBlock
    };
    return createBlockRegistry(defaults, blockRenderers);
  }, [blockRenderers]);
  const getMessageTextFromDom = () => {
    const el = containerRef.current;
    return (el?.innerText || "").trim();
  };
  return /* @__PURE__ */ jsx21(
    "div",
    {
      ref: containerRef,
      className: clsx20(
        "min-w-0 select-text",
        "flex flex-col gap-4",
        className
      ),
      "data-message-body": "true",
      children: normalized.blocks.map((block) => {
        const Renderer = registry[block.kind];
        if (!Renderer) return null;
        return /* @__PURE__ */ jsx21(
          Renderer,
          {
            block,
            surface,
            getMessageTextFromDom
          },
          block.id
        );
      })
    }
  );
};

// src/themes/neobrutalism/Bubble.tsx
import { jsx as jsx22, jsxs as jsxs20 } from "react/jsx-runtime";
var NeoBubble = ({ role, content, parts, isStreaming, id }) => {
  const theme = useChatTheme();
  const isUser = role === "user";
  const baseStyle = "border-2 border-black rounded-xl p-3 md:p-4 max-w-[85%] text-sm md:text-base font-medium";
  const userStyle = "bg-blue-500 text-white self-end shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]";
  const aiStyle = "bg-white text-slate-900 self-start shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]";
  const surface = theme.surface || "app";
  return /* @__PURE__ */ jsxs20(
    motion2.div,
    {
      initial: { opacity: 0, y: 10, scale: 0.95 },
      animate: { opacity: 1, y: 0, scale: 1 },
      className: clsx21(baseStyle, isUser ? userStyle : aiStyle),
      "data-message-id": id || void 0,
      "data-message-root": "true",
      children: [
        /* @__PURE__ */ jsx22("div", { className: "min-w-0", children: /* @__PURE__ */ jsx22(
          MessageBody,
          {
            messageId: id || "legacy",
            role,
            parts,
            content,
            isStreaming,
            surface,
            blockRenderers: theme.blockRenderers
          }
        ) }),
        isStreaming && /* @__PURE__ */ jsx22("span", { className: "inline-block w-2 h-4 ml-1 bg-black animate-pulse align-middle" })
      ]
    }
  );
};

// src/themes/neobrutalism/Composer.tsx
import { useState as useState11 } from "react";
import { Send } from "lucide-react";
import { jsx as jsx23, jsxs as jsxs21 } from "react/jsx-runtime";
var NeoComposer = ({ onSend, isLoading, placeholder }) => {
  const [text, setText] = useState11("");
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim() || isLoading) return;
    onSend(text);
    setText("");
  };
  return /* @__PURE__ */ jsxs21("form", { onSubmit: handleSubmit, className: "p-4 border-t-2 border-black bg-slate-50 flex gap-2", children: [
    /* @__PURE__ */ jsx23(
      "input",
      {
        className: "flex-1 bg-white border-2 border-black rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all active:translate-x-[1px] active:translate-y-[1px] active:shadow-none",
        placeholder: placeholder || "Type a message...",
        value: text,
        onChange: (e) => setText(e.target.value),
        disabled: isLoading
      }
    ),
    /* @__PURE__ */ jsx23(
      "button",
      {
        type: "submit",
        disabled: isLoading || !text.trim(),
        className: "bg-emerald-500 text-white border-2 border-black rounded-lg p-3 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-y-0 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed",
        children: /* @__PURE__ */ jsx23(Send, { size: 20 })
      }
    )
  ] });
};

// src/themes/neobrutalism/index.tsx
import { jsx as jsx24, jsxs as jsxs22 } from "react/jsx-runtime";
var NeoWindow = ({ children, className }) => /* @__PURE__ */ jsx24("div", { className: `flex flex-col h-full bg-slate-100 ${className}`, children });
var NeoTyping = () => /* @__PURE__ */ jsx24("div", { className: "self-start bg-white border-2 border-black rounded-full px-4 py-2 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]", children: /* @__PURE__ */ jsxs22("div", { className: "flex gap-1", children: [
  /* @__PURE__ */ jsx24("div", { className: "w-2 h-2 bg-black rounded-full animate-bounce", style: { animationDelay: "0ms" } }),
  /* @__PURE__ */ jsx24("div", { className: "w-2 h-2 bg-black rounded-full animate-bounce", style: { animationDelay: "150ms" } }),
  /* @__PURE__ */ jsx24("div", { className: "w-2 h-2 bg-black rounded-full animate-bounce", style: { animationDelay: "300ms" } })
] }) });
var NeoTool = ({ name }) => /* @__PURE__ */ jsxs22("div", { className: "text-xs font-mono text-slate-500 flex items-center gap-2 my-1", children: [
  /* @__PURE__ */ jsx24("span", { className: "w-4 h-4 bg-emerald-100 border border-black rounded flex items-center justify-center text-[10px]", children: "\u2713" }),
  name,
  " executed"
] });
var NeobrutalismTheme = {
  surface: "app",
  MessageBubble: NeoBubble,
  Composer: NeoComposer,
  ChatWindow: NeoWindow,
  TypingIndicator: NeoTyping,
  ToolResult: NeoTool,
  ErrorState: ({ error }) => /* @__PURE__ */ jsx24("div", { className: "p-4 bg-red-100 border-2 border-black text-red-900", children: error })
};

// src/contexts/ThemeContext.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var ThemeContext = createContext(NeobrutalismTheme);
var ChatUIProvider = ({
  theme = NeobrutalismTheme,
  children
}) => {
  return /* @__PURE__ */ jsx25(ThemeContext.Provider, { value: theme, children });
};
var useChatTheme = () => useContext(ThemeContext);

// src/renderers/MessageList.tsx
import { useEffect as useEffect9, useRef as useRef7 } from "react";
import { useChat as useChat3, useChatClient as useChatClient7 } from "@dopomogai/chat-react";
import { jsx as jsx26, jsxs as jsxs23 } from "react/jsx-runtime";
var SCROLL_RESTORE_MAX_ATTEMPTS = 12;
var SCROLL_RESTORE_TOLERANCE = 4;
var SCROLL_SAVE_THROTTLE = 80;
var MessageList = ({ className }) => {
  const {
    messages,
    isLoading = false,
    isReasoning = false,
    activeId,
    status,
    error,
    errorCode,
    retryAfterSec
  } = useChat3();
  const client = useChatClient7();
  const Theme = useChatTheme();
  const containerRef = useRef7(null);
  const isRestoringRef = useRef7(false);
  const restoreAttemptsRef = useRef7(0);
  const lastSaveTsRef = useRef7(0);
  const scrollRafRef = useRef7(null);
  const resizeObserverRef = useRef7(null);
  const setPixelScrollTop = (cid, top) => {
    client.setScrollPosition(cid, top);
  };
  const setAnchor = (cid, anchor) => {
    client.setScrollAnchor(cid, anchor);
  };
  const getSavedAnchor = () => {
    if (!activeId) return null;
    return client.getScrollAnchor(activeId);
  };
  const handleScrollWork = () => {
    if (!activeId || !containerRef.current) return;
    const now = performance.now();
    if (now - lastSaveTsRef.current < SCROLL_SAVE_THROTTLE) return;
    lastSaveTsRef.current = now;
    setPixelScrollTop(activeId, containerRef.current.scrollTop);
    const el = containerRef.current;
    const nodes = Array.from(el.querySelectorAll('[data-message-root="true"][data-message-id]'));
    const containerTop = el.getBoundingClientRect().top;
    let chosen = null;
    for (const n of nodes) {
      const id = n.getAttribute("data-message-id") || "";
      if (!id) continue;
      const top = n.getBoundingClientRect().top - containerTop;
      if (top >= 0) {
        chosen = { id, offset: Math.round(top) };
        break;
      }
    }
    if (chosen) {
      setAnchor(activeId, { messageId: chosen.id, offset: chosen.offset });
    }
  };
  const handleScroll = () => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      handleScrollWork();
    });
  };
  useEffect9(() => {
    const el = containerRef.current;
    if (!el) return;
    if (isRestoringRef.current) return;
    const distanceToBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    const isNearBottom = distanceToBottom < 200;
    if (isNearBottom) {
      const timer = setTimeout(() => {
        el.scrollTop = el.scrollHeight;
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [messages.length, isLoading, isReasoning]);
  useEffect9(() => {
    const el = containerRef.current;
    if (!el || !activeId) return;
    const savedPixel = client.getScrollPosition(activeId);
    const savedAnchor = getSavedAnchor();
    isRestoringRef.current = true;
    restoreAttemptsRef.current = 0;
    const tryAnchorRestore = () => {
      if (!el) return false;
      restoreAttemptsRef.current += 1;
      const anchor = getSavedAnchor();
      if (!anchor) return false;
      const node = el.querySelector(
        `[data-message-root="true"][data-message-id="${anchor.messageId}"]`
      );
      if (!node) return false;
      const containerTop = el.getBoundingClientRect().top;
      const relTop = node.getBoundingClientRect().top - containerTop;
      const delta = Math.round(relTop - anchor.offset);
      if (Math.abs(delta) <= SCROLL_RESTORE_TOLERANCE) return true;
      el.scrollTop = el.scrollTop + delta;
      return Math.abs(node.getBoundingClientRect().top - containerTop - anchor.offset) <= SCROLL_RESTORE_TOLERANCE;
    };
    const tryPixelRestore = () => {
      el.scrollTop = savedPixel;
      const delta = Math.abs(el.scrollTop - savedPixel);
      return delta <= SCROLL_RESTORE_TOLERANCE;
    };
    const attempt = () => {
      let done = false;
      if (savedAnchor) done = tryAnchorRestore();
      if (!done && !savedAnchor) done = tryPixelRestore();
      if (!done) {
        if (restoreAttemptsRef.current < SCROLL_RESTORE_MAX_ATTEMPTS) {
          requestAnimationFrame(attempt);
        } else {
          if (!savedAnchor) requestAnimationFrame(attempt);
          else el.scrollTop = savedPixel;
          isRestoringRef.current = false;
        }
      } else {
        setTimeout(() => {
          isRestoringRef.current = false;
        }, 50);
      }
    };
    requestAnimationFrame(attempt);
    if ("ResizeObserver" in window && !resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        if (!isRestoringRef.current) return;
        requestAnimationFrame(attempt);
      });
      resizeObserverRef.current.observe(el);
    }
    return () => {
      isRestoringRef.current = false;
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [activeId, client]);
  if (messages.length === 0) {
    return /* @__PURE__ */ jsxs23(
      "div",
      {
        ref: containerRef,
        onScroll: handleScroll,
        className: `flex flex-col overflow-y-auto overflow-x-hidden custom-scrollbar ${className || ""}`,
        children: [
          /* @__PURE__ */ jsx26("div", { className: "flex-1 min-h-0" }),
          /* @__PURE__ */ jsx26("div", { className: "h-24" })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs23(
    "div",
    {
      ref: containerRef,
      onScroll: handleScroll,
      className: `flex flex-col gap-6 p-4 overflow-y-auto overflow-x-hidden custom-scrollbar ${className || ""}`,
      children: [
        messages.map((msg) => {
          const fallbackContent = (msg.parts || []).filter((p) => p.type === "text").map((p) => p.content || "").join("");
          const isStreamingCursor = msg.role === "assistant" && msg.status === "streaming" && !isReasoning;
          return /* @__PURE__ */ jsx26(
            Theme.MessageBubble,
            {
              id: msg.id,
              role: msg.role,
              parts: msg.parts,
              content: fallbackContent,
              timestamp: msg.createdAt,
              isStreaming: isStreamingCursor
            },
            msg.id
          );
        }),
        status === "ERROR" && error && /* @__PURE__ */ jsx26("div", { className: "max-w-2xl mx-auto w-full", children: /* @__PURE__ */ jsx26(
          Theme.ErrorState,
          {
            error,
            errorCode,
            retryAfterSec,
            onRetry: async () => {
              try {
                await client.resendPending(activeId);
              } catch (e) {
                console.error("Retry failed", e);
              }
            }
          }
        ) }),
        isLoading && messages[messages.length - 1]?.role === "user" && !isReasoning && /* @__PURE__ */ jsx26(Theme.TypingIndicator, {}),
        /* @__PURE__ */ jsx26("div", { className: "h-4" })
      ]
    }
  );
};

// src/renderers/components/CopyMenu.tsx
import { useMemo as useMemo16, useState as useState12 } from "react";
import clsx22 from "clsx";
import { Copy as Copy3, Check as Check4, ChevronDown as ChevronDown3 } from "lucide-react";
import { Fragment as Fragment7, jsx as jsx27, jsxs as jsxs24 } from "react/jsx-runtime";
var CopyMenu = ({
  getMarkdown,
  getFormattedText,
  allowMarkdown = true,
  allowFormattedText = true,
  size = "sm",
  className
}) => {
  const [open, setOpen] = useState12(false);
  const [copiedKey, setCopiedKey] = useState12(null);
  const items = useMemo16(() => {
    const list = [];
    if (allowMarkdown && getMarkdown) {
      list.push({
        key: "md",
        label: "Copy as Markdown",
        action: async () => copyText((getMarkdown() || "").trim())
      });
    }
    if (allowFormattedText && getFormattedText) {
      list.push({
        key: "text",
        label: "Copy formatted text",
        action: async () => copyText((getFormattedText() || "").trim())
      });
    }
    return list;
  }, [allowMarkdown, allowFormattedText, getMarkdown, getFormattedText]);
  const btnSize = size === "sm" ? "p-1.5" : "p-2";
  if (items.length === 0) return null;
  const handleCopy = async (key, action) => {
    try {
      await action();
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1200);
      setOpen(false);
    } catch (e) {
      console.error("Copy failed", e);
    }
  };
  return /* @__PURE__ */ jsxs24("div", { className: clsx22("relative", className), children: [
    /* @__PURE__ */ jsxs24(
      "button",
      {
        type: "button",
        onClick: () => setOpen((v) => !v),
        className: clsx22(
          btnSize,
          "select-none",
          "rounded-md border border-slate-200 bg-white shadow-sm",
          "text-slate-600 hover:text-slate-900 hover:bg-slate-50",
          "transition-colors",
          "inline-flex items-center gap-1"
        ),
        title: "Copy",
        children: [
          copiedKey ? /* @__PURE__ */ jsx27(Check4, { size: 14, className: "text-emerald-600" }) : /* @__PURE__ */ jsx27(Copy3, { size: 14 }),
          /* @__PURE__ */ jsx27(ChevronDown3, { size: 14, className: "opacity-60" })
        ]
      }
    ),
    open && /* @__PURE__ */ jsxs24(Fragment7, { children: [
      /* @__PURE__ */ jsx27(
        "button",
        {
          className: "fixed inset-0 z-[9998] cursor-default",
          onClick: () => setOpen(false),
          "aria-label": "Close copy menu"
        }
      ),
      /* @__PURE__ */ jsx27(
        "div",
        {
          className: clsx22(
            "absolute right-0 mt-2 z-[9999] min-w-[210px]",
            "rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden"
          ),
          children: items.map((item) => /* @__PURE__ */ jsx27(
            "button",
            {
              type: "button",
              onClick: () => handleCopy(item.key, item.action),
              className: clsx22(
                "w-full text-left px-3 py-2 text-sm",
                "hover:bg-slate-50 text-slate-700 hover:text-slate-900",
                "transition-colors"
              ),
              children: item.label
            },
            item.key
          ))
        }
      )
    ] })
  ] });
};

// src/renderers/components/toolbars/CompactActionBar.tsx
import { useEffect as useEffect11, useRef as useRef8, useState as useState14 } from "react";
import clsx24 from "clsx";
import { Copy as Copy4, Check as Check5, Edit2, Trash2 as Trash23 } from "lucide-react";

// src/renderers/components/toolbars/FloatingMenu.tsx
import { useEffect as useEffect10, useLayoutEffect, useState as useState13 } from "react";
import { createPortal as createPortal5 } from "react-dom";
import clsx23 from "clsx";
import { jsx as jsx28 } from "react/jsx-runtime";
function isSSR2() {
  return typeof document === "undefined";
}
var FloatingMenu = ({
  anchorEl,
  open,
  placement = "bottom-end",
  onClose,
  items,
  className,
  onPointerEnter,
  onPointerLeave,
  menuRef
}) => {
  const [pos, setPos] = useState13(null);
  const recompute = () => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    setPos({
      left: r.left + scrollX,
      top: r.bottom + scrollY + 6
    });
  };
  useLayoutEffect(() => {
    if (open) recompute();
  }, [open, anchorEl]);
  useEffect10(() => {
    if (!open) return;
    const onWin = () => recompute();
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("resize", onWin);
    return () => {
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("resize", onWin);
    };
  }, [open]);
  if (!open || isSSR2()) return null;
  return createPortal5(
    /* @__PURE__ */ jsx28(
      "div",
      {
        ref: (node) => {
          if (!menuRef) return;
          menuRef.current = node;
        },
        className: clsx23(
          "fixed z-[9999] min-w-[200px] rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden",
          className
        ),
        style: {
          left: pos?.left ?? -9999,
          top: pos?.top ?? -9999,
          transform: placement === "bottom-end" ? "translateX(calc(-100% + 28px))" : "translateX(0)"
        },
        role: "menu",
        onPointerEnter,
        onPointerLeave,
        children: items.map((it) => /* @__PURE__ */ jsx28(
          "button",
          {
            type: "button",
            onClick: () => {
              it.onClick();
              onClose();
            },
            className: "w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700 hover:text-slate-900 transition-colors",
            role: "menuitem",
            children: it.label
          },
          it.key
        ))
      }
    ),
    document.body
  );
};

// src/renderers/components/toolbars/CompactActionBar.tsx
import { jsx as jsx29, jsxs as jsxs25 } from "react/jsx-runtime";
var CompactActionBar = ({
  surface = "app",
  align = "left",
  getMarkdown,
  getFormattedText,
  defaultCopyMode = "formatted",
  allowEdit = false,
  onEdit,
  allowDelete = true,
  onDelete,
  onCopyMenuOpenChange,
  onHoverChange,
  className
}) => {
  const [copied, setCopied] = useState14(false);
  const [menuOpen, setMenuOpen] = useState14(false);
  const copyBtnRef = useRef8(null);
  const menuElRef = useRef8(null);
  const closeTimer = useRef8(null);
  const overAnchor = useRef8(false);
  const overMenu = useRef8(false);
  const hasMarkdown = !!getMarkdown;
  const hasFormatted = !!getFormattedText;
  const canOpenMenu = hasMarkdown && hasFormatted;
  const setOpen = (v) => {
    setMenuOpen(v);
    onCopyMenuOpenChange?.(v);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      if (!overAnchor.current && !overMenu.current) setOpen(false);
    }, 180);
  };
  useEffect11(() => () => cancelClose(), []);
  useEffect11(() => {
    if (!menuOpen) return;
    const onDocPointerDown = (e) => {
      const target = e.target;
      const anchor = copyBtnRef.current;
      const menu = menuElRef.current;
      const insideAnchor = !!(anchor && target && anchor.contains(target));
      const insideMenu = !!(menu && target && menu.contains(target));
      if (!insideAnchor && !insideMenu) {
        overAnchor.current = false;
        overMenu.current = false;
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        overAnchor.current = false;
        overMenu.current = false;
        setOpen(false);
      }
    };
    const onScroll = () => {
      overAnchor.current = false;
      overMenu.current = false;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [menuOpen]);
  const doCopy = async (mode) => {
    const text = mode === "markdown" ? (getMarkdown?.() || "").trim() : (getFormattedText?.() || "").trim();
    if (!text) return;
    await copyText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 900);
  };
  const onMainCopy = (e) => {
    e.stopPropagation();
    if (defaultCopyMode === "markdown" && hasMarkdown) return void doCopy("markdown");
    if (hasFormatted) return void doCopy("formatted");
    if (hasMarkdown) return void doCopy("markdown");
  };
  const openMenu = () => {
    if (!canOpenMenu) return;
    cancelClose();
    setOpen(true);
  };
  return /* @__PURE__ */ jsxs25(
    "div",
    {
      className: clsx24(
        "flex items-center bg-white border border-slate-200 shadow-md rounded-full p-1 gap-0.5",
        "pointer-events-auto select-none",
        className
      ),
      onPointerDown: (e) => e.stopPropagation(),
      onMouseDown: (e) => e.stopPropagation(),
      onPointerEnter: () => onHoverChange?.(true),
      onPointerLeave: () => onHoverChange?.(false),
      children: [
        (hasMarkdown || hasFormatted) && /* @__PURE__ */ jsx29(
          "button",
          {
            ref: copyBtnRef,
            type: "button",
            onClick: onMainCopy,
            onPointerEnter: () => {
              overAnchor.current = true;
              openMenu();
            },
            onPointerLeave: () => {
              overAnchor.current = false;
              scheduleClose();
            },
            className: clsx24(
              "p-1.5 rounded-full transition-colors",
              copied ? "text-emerald-700 bg-emerald-50" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            ),
            title: copied ? "Copied" : "Copy (click). Hover for options.",
            children: copied ? /* @__PURE__ */ jsx29(Check5, { size: 14, className: "text-emerald-600" }) : /* @__PURE__ */ jsx29(Copy4, { size: 14 })
          }
        ),
        allowEdit && onEdit && surface !== "widget" && /* @__PURE__ */ jsx29(
          "button",
          {
            type: "button",
            onClick: (e) => {
              e.stopPropagation();
              onEdit();
            },
            className: "p-1.5 rounded-full text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors",
            title: "Edit message",
            children: /* @__PURE__ */ jsx29(Edit2, { size: 14 })
          }
        ),
        allowDelete && onDelete && /* @__PURE__ */ jsx29(
          "button",
          {
            type: "button",
            onClick: (e) => {
              e.stopPropagation();
              onDelete();
            },
            className: "p-1.5 rounded-full text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors",
            title: "Delete message",
            children: /* @__PURE__ */ jsx29(Trash23, { size: 14 })
          }
        ),
        /* @__PURE__ */ jsx29(
          FloatingMenu,
          {
            anchorEl: copyBtnRef.current,
            open: menuOpen,
            placement: align === "right" ? "bottom-end" : "bottom-start",
            onClose: () => setOpen(false),
            menuRef: menuElRef,
            onPointerEnter: () => {
              overMenu.current = true;
              cancelClose();
              setOpen(true);
            },
            onPointerLeave: () => {
              overMenu.current = false;
              scheduleClose();
            },
            items: [
              ...hasFormatted ? [{ key: "text", label: "Copy formatted text", onClick: () => doCopy("formatted") }] : [],
              ...hasMarkdown ? [{ key: "md", label: "Copy as Markdown", onClick: () => doCopy("markdown") }] : []
            ]
          }
        )
      ]
    }
  );
};

// src/renderers/components/toolbars/MessageHoverToolbars.tsx
import { useEffect as useEffect12, useState as useState15 } from "react";
import clsx25 from "clsx";
import { jsx as jsx30, jsxs as jsxs26 } from "react/jsx-runtime";
var MessageHoverToolbars = ({
  containerRef,
  surface = "app",
  align = "left",
  getMarkdown,
  getFormattedText,
  defaultCopyMode = "formatted",
  allowEdit = false,
  onEdit,
  allowDelete = true,
  onDelete,
  className
}) => {
  const [visible, setVisible] = useState15(false);
  const [region, setRegion] = useState15("top");
  const [menuLock, setMenuLock] = useState15(false);
  const [hoverLock, setHoverLock] = useState15(false);
  useEffect12(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const inside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!inside) {
        if (!hoverLock && !menuLock) setVisible(false);
        return;
      }
      setVisible(true);
      if (menuLock || hoverLock) return;
      const relY = (e.clientY - rect.top) / Math.max(rect.height, 1);
      setRegion(relY <= 0.5 ? "top" : "bottom");
    };
    const onEnter = () => setVisible(true);
    const onLeave = () => {
      if (!hoverLock && !menuLock) setVisible(false);
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [containerRef.current, menuLock, hoverLock]);
  const forcedVisible = visible || hoverLock || menuLock;
  if (!getMarkdown && !getFormattedText && !allowEdit && !allowDelete) return null;
  const sideClass = align === "right" ? "right-2" : "left-2";
  return /* @__PURE__ */ jsxs26("div", { className: clsx25("absolute inset-0 z-20 pointer-events-none", className), "aria-hidden": true, children: [
    /* @__PURE__ */ jsx30(
      "div",
      {
        className: clsx25(
          "absolute top-1 transition-opacity duration-100",
          sideClass,
          forcedVisible && region === "top" ? "opacity-100" : "opacity-0"
        ),
        children: /* @__PURE__ */ jsx30(
          CompactActionBar,
          {
            surface,
            align,
            getMarkdown,
            getFormattedText,
            defaultCopyMode,
            allowEdit,
            onEdit,
            allowDelete,
            onDelete,
            onCopyMenuOpenChange: setMenuLock,
            onHoverChange: setHoverLock
          }
        )
      }
    ),
    /* @__PURE__ */ jsx30(
      "div",
      {
        className: clsx25(
          "absolute bottom-1 transition-opacity duration-100",
          sideClass,
          forcedVisible && region === "bottom" ? "opacity-100" : "opacity-0"
        ),
        children: /* @__PURE__ */ jsx30(
          CompactActionBar,
          {
            surface,
            align,
            getMarkdown,
            getFormattedText,
            defaultCopyMode,
            allowEdit,
            onEdit,
            allowDelete,
            onDelete,
            onCopyMenuOpenChange: setMenuLock,
            onHoverChange: setHoverLock
          }
        )
      }
    )
  ] });
};

// src/themes/glass/GlassWindow.tsx
import clsx26 from "clsx";
import { jsx as jsx31, jsxs as jsxs27 } from "react/jsx-runtime";
var GlassWindow = ({
  children,
  className
}) => {
  return /* @__PURE__ */ jsxs27("div", { className: clsx26("flex flex-col h-full relative font-sans text-slate-900 overflow-hidden", className), children: [
    /* @__PURE__ */ jsxs27("div", { className: "absolute inset-0 z-0 bg-slate-50", children: [
      /* @__PURE__ */ jsx31("div", { className: "absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" }),
      /* @__PURE__ */ jsx31("div", { className: "absolute left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-blue-400 opacity-20 blur-[100px]" }),
      /* @__PURE__ */ jsx31("div", { className: "absolute right-0 bottom-0 -z-10 h-[310px] w-[310px] rounded-full bg-emerald-400 opacity-10 blur-[100px]" })
    ] }),
    /* @__PURE__ */ jsx31("div", { className: "relative z-10 flex flex-col h-full backdrop-blur-[1px]", children })
  ] });
};

// src/themes/glass/GlassBubble.tsx
import { useEffect as useEffect13, useMemo as useMemo17, useRef as useRef9, useState as useState16 } from "react";
import clsx27 from "clsx";
import { Bot, User, Sparkles, Check as Check6, MessageSquare, Paperclip as Paperclip2, GitBranch } from "lucide-react";
import { useChat as useChat4, useChatClient as useChatClient8, useChatConfig } from "@dopomogai/chat-react";
import { Fragment as Fragment8, jsx as jsx32, jsxs as jsxs28 } from "react/jsx-runtime";
var GlassBubble = ({
  role,
  content,
  parts,
  isStreaming,
  timestamp,
  id
}) => {
  const client = useChatClient8();
  const { messages, activeId } = useChat4();
  const theme = useChatTheme();
  const config = useChatConfig();
  const agentName = config?.agentName || "AI Agent";
  const features = config?.features || {};
  const threadsEnabled = features.threadsEnabled === true;
  const commentsEnabled = features.commentsEnabled === true;
  const attachmentsEnabled = features.attachmentsEnabled !== false;
  const hasQuickActions = threadsEnabled || commentsEnabled || attachmentsEnabled;
  const isUser = role === "user";
  const surface = theme.surface || "app";
  const [isEditing, setIsEditing] = useState16(false);
  const [editValue, setEditValue] = useState16(content || "");
  const [deleteOpen, setDeleteOpen] = useState16(false);
  const bodyRef = useRef9(null);
  const bubbleRef = useRef9(null);
  const [selText, setSelText] = useState16("");
  const [selBox, setSelBox] = useState16(null);
  const selectionMenuVisible = !!selText && !!selBox;
  const messageIndex = useMemo17(() => {
    if (!id) return -1;
    return messages.findIndex((m) => m.id === id);
  }, [messages, id]);
  useEffect13(() => {
    if (!isEditing) setEditValue(content || "");
  }, [content, isEditing]);
  useEffect13(() => {
    const onMouseUp = () => {
      try {
        if (!bodyRef.current) return hideSelectionMenu();
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed) return hideSelectionMenu();
        const range = sel.getRangeAt(0);
        if (!range) return hideSelectionMenu();
        const container = bodyRef.current;
        const common = range.commonAncestorContainer;
        const nodeEl = common.nodeType === Node.ELEMENT_NODE ? common : common.parentElement;
        if (!nodeEl || !container.contains(nodeEl)) return hideSelectionMenu();
        const rect = range.getBoundingClientRect();
        if (!rect || rect.width === 0 && rect.height === 0) return hideSelectionMenu();
        const txt = sel.toString().trim();
        if (!txt) return hideSelectionMenu();
        const offsetX = 0;
        const offsetY = -8;
        setSelText(txt);
        setSelBox({ left: rect.left + window.scrollX + rect.width / 2 + offsetX, top: rect.top + window.scrollY + offsetY });
      } catch {
        hideSelectionMenu();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") hideSelectionMenu();
    };
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("keyup", onKeyDown);
    return () => {
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("keyup", onKeyDown);
    };
  }, [bodyRef.current]);
  const hideSelectionMenu = () => {
    setSelText("");
    setSelBox(null);
  };
  const normalized = useMemo17(() => {
    return normalizeMessage({
      messageId: id || "legacy",
      role,
      parts,
      content,
      isStreaming,
      surface
    });
  }, [id, role, parts, content, isStreaming, surface]);
  const getMarkdown = () => normalized.toMarkdown();
  const getFormattedText = () => (bodyRef.current?.innerText || "").trim();
  const doEditInPlace = async (rerun) => {
    if (!id || !editValue.trim()) return;
    const cid = activeId;
    if (!cid) return;
    const ok = await client.conversationManager.editMessageInPlace(cid, id, { newText: editValue });
    if (ok && rerun) {
      await client.conversationManager.rerunFromIndex(cid, id);
    }
  };
  const doDeleteMessage = async () => {
    if (id) client.deleteMessage(id);
  };
  const onDeleteClick = () => {
    if (shouldAskDeleteConfirm()) {
      setDeleteOpen(true);
    } else {
      void doDeleteMessage();
    }
  };
  const openThreadPanel = () => {
    if (!threadsEnabled) return;
    if (!id || messageIndex < 0) return;
    window.dispatchEvent(
      new CustomEvent("dopomogai:open-thread", {
        detail: { messageId: id, messageIndex }
      })
    );
  };
  const openAttachmentManager = () => {
    if (!attachmentsEnabled) return;
    if (!id) return;
    window.dispatchEvent(
      new CustomEvent("dopomogai:manage-attachments", {
        detail: { messageId: id }
      })
    );
  };
  const openCommentsPanel = () => {
    if (!commentsEnabled) return;
    if (!id || messageIndex < 0) return;
    window.dispatchEvent(
      new CustomEvent("dopomogai:open-comments", {
        detail: { messageId: id, messageIndex }
      })
    );
  };
  const addCommentFromSelection = () => {
    if (!commentsEnabled) return;
    if (!id || !selText) return;
    window.dispatchEvent(
      new CustomEvent("dopomogai:add-comment", {
        detail: { messageId: id, selection: selText }
      })
    );
    hideSelectionMenu();
  };
  const quoteSelectionToComposer = () => {
    if (!selText) return;
    window.dispatchEvent(
      new CustomEvent("dopomogai:quote-selection", {
        detail: { text: selText }
      })
    );
    hideSelectionMenu();
  };
  const copySelection = async () => {
    if (!selText) return;
    await copyText(selText);
    hideSelectionMenu();
  };
  const align = isUser ? "right" : "left";
  return /* @__PURE__ */ jsxs28(
    "div",
    {
      className: clsx27("group flex w-full mb-6 relative gap-4 transition-all duration-300 px-1", isUser ? "flex-row-reverse" : "flex-row"),
      "data-message-id": id || void 0,
      "data-message-root": "true",
      ref: bubbleRef,
      children: [
        /* @__PURE__ */ jsx32(
          "div",
          {
            className: clsx27(
              "w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-1 shadow-sm border select-none transition-transform duration-300",
              isUser ? "bg-slate-200 border-slate-300 text-slate-500" : "bg-gradient-to-br from-blue-600 to-indigo-600 text-white border-transparent shadow-blue-500/20"
            ),
            children: isUser ? /* @__PURE__ */ jsx32(User, { size: 16 }) : /* @__PURE__ */ jsx32(Bot, { size: 18 })
          }
        ),
        /* @__PURE__ */ jsxs28(
          "div",
          {
            className: clsx27(
              "relative px-6 py-5 text-[15px] leading-7 shadow-sm border max-w-[90%] lg:max-w-[85%] min-w-0 rounded-3xl transition-all duration-300",
              isUser ? "bg-slate-50/80 border-slate-200 text-slate-900 rounded-tr-sm" : "bg-white/80 backdrop-blur-xl border-white/60 text-slate-900 rounded-tl-sm shadow-slate-200/50",
              isEditing && "w-full max-w-full ring-2 ring-blue-400 bg-white z-20 shadow-2xl"
            ),
            children: [
              !isEditing && /* @__PURE__ */ jsxs28(
                "div",
                {
                  className: clsx27(
                    "flex items-center gap-2 text-[11px] font-bold tracking-wider uppercase mb-3 select-none",
                    isUser ? "flex-row-reverse text-slate-400" : "text-blue-600"
                  ),
                  children: [
                    /* @__PURE__ */ jsx32("span", { children: isUser ? "You" : agentName }),
                    timestamp && /* @__PURE__ */ jsxs28("span", { className: "opacity-40 font-normal normal-case tracking-normal text-[10px]", children: [
                      "\u2022 ",
                      new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                    ] }),
                    role === "assistant" && !isStreaming && /* @__PURE__ */ jsx32(Sparkles, { size: 10, className: "text-emerald-400" })
                  ]
                }
              ),
              !isEditing && !isStreaming && /* @__PURE__ */ jsx32(
                MessageHoverToolbars,
                {
                  containerRef: bubbleRef,
                  surface,
                  align,
                  getMarkdown,
                  getFormattedText,
                  defaultCopyMode: "formatted",
                  allowEdit: true,
                  onEdit: () => setIsEditing(true),
                  allowDelete: true,
                  onDelete: onDeleteClick
                }
              ),
              !isEditing && hasQuickActions && /* @__PURE__ */ jsx32(
                "div",
                {
                  className: clsx27(
                    "absolute -top-3",
                    isUser ? "right-6" : "left-6",
                    "opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none"
                  ),
                  children: /* @__PURE__ */ jsxs28("div", { className: "flex items-center gap-1 bg-white/80 border border-slate-200 rounded-full shadow-sm p-0.5 pointer-events-auto", children: [
                    threadsEnabled && /* @__PURE__ */ jsxs28(
                      "button",
                      {
                        type: "button",
                        onClick: openThreadPanel,
                        className: "px-2 py-1 text-[11px] rounded-full hover:bg-slate-100 text-slate-600 flex items-center gap-1",
                        title: "Reply in thread",
                        children: [
                          /* @__PURE__ */ jsx32(GitBranch, { size: 12 }),
                          " Thread"
                        ]
                      }
                    ),
                    attachmentsEnabled && /* @__PURE__ */ jsxs28(
                      "button",
                      {
                        type: "button",
                        onClick: openAttachmentManager,
                        className: "px-2 py-1 text-[11px] rounded-full hover:bg-slate-100 text-slate-600 flex items-center gap-1",
                        title: "Manage attachments for this message",
                        children: [
                          /* @__PURE__ */ jsx32(Paperclip2, { size: 12 }),
                          " Attachments"
                        ]
                      }
                    ),
                    commentsEnabled && /* @__PURE__ */ jsxs28(
                      "button",
                      {
                        type: "button",
                        onClick: openCommentsPanel,
                        className: "px-2 py-1 text-[11px] rounded-full hover:bg-slate-100 text-slate-600 flex items-center gap-1",
                        title: "Open comments for this message",
                        children: [
                          /* @__PURE__ */ jsx32(MessageSquare, { size: 12 }),
                          " Comments"
                        ]
                      }
                    )
                  ] })
                }
              ),
              isEditing ? /* @__PURE__ */ jsxs28("div", { className: "flex flex-col gap-3 animate-in fade-in zoom-in-95 duration-200", children: [
                /* @__PURE__ */ jsx32(
                  "textarea",
                  {
                    value: editValue,
                    onChange: (e) => setEditValue(e.target.value),
                    className: "w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 min-h-[120px] font-sans text-sm resize-none leading-relaxed shadow-inner",
                    autoFocus: true
                  }
                ),
                /* @__PURE__ */ jsxs28("div", { className: "flex flex-wrap justify-end gap-2", children: [
                  /* @__PURE__ */ jsx32(
                    "button",
                    {
                      onClick: () => setIsEditing(false),
                      className: "px-3 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100 text-xs font-bold uppercase tracking-wide transition-colors",
                      children: "Cancel"
                    }
                  ),
                  /* @__PURE__ */ jsxs28(
                    "button",
                    {
                      onClick: async () => {
                        await doEditInPlace(false);
                        setIsEditing(false);
                      },
                      className: "flex items-center gap-1 px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold uppercase tracking-wide shadow-md transition-all active:scale-95",
                      children: [
                        /* @__PURE__ */ jsx32(Check6, { size: 12 }),
                        " Save"
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsxs28(
                    "button",
                    {
                      onClick: async () => {
                        await doEditInPlace(true);
                        setIsEditing(false);
                      },
                      className: "flex items-center gap-1 px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wide shadow-md transition-all active:scale-95",
                      title: "Save changes and remove subsequent turns to regenerate",
                      children: [
                        /* @__PURE__ */ jsx32(Sparkles, { size: 12 }),
                        " Save & Rerun"
                      ]
                    }
                  )
                ] })
              ] }) : /* @__PURE__ */ jsx32("div", { ref: bodyRef, className: "min-w-0 select-text", children: /* @__PURE__ */ jsx32(
                MessageBody,
                {
                  messageId: id || "legacy",
                  role,
                  parts,
                  content,
                  isStreaming,
                  surface,
                  blockRenderers: theme.blockRenderers
                }
              ) })
            ]
          }
        ),
        selectionMenuVisible && /* @__PURE__ */ jsx32(
          "div",
          {
            className: "fixed z-[9999] -translate-x-1/2 -translate-y-full",
            style: { left: selBox.left, top: selBox.top },
            children: /* @__PURE__ */ jsxs28("div", { className: "bg-white border border-slate-200 rounded-lg shadow-md flex items-center", children: [
              commentsEnabled && /* @__PURE__ */ jsxs28(Fragment8, { children: [
                /* @__PURE__ */ jsx32(
                  "button",
                  {
                    onClick: addCommentFromSelection,
                    className: "px-2 py-1 text-[12px] text-slate-700 hover:bg-slate-50",
                    title: "Add comment",
                    children: "Comment"
                  }
                ),
                /* @__PURE__ */ jsx32("div", { className: "w-px h-4 bg-slate-200" })
              ] }),
              /* @__PURE__ */ jsx32(
                "button",
                {
                  onClick: quoteSelectionToComposer,
                  className: "px-2 py-1 text-[12px] text-slate-700 hover:bg-slate-50",
                  title: "Quote in composer",
                  children: "Quote"
                }
              ),
              /* @__PURE__ */ jsx32("div", { className: "w-px h-4 bg-slate-200" }),
              /* @__PURE__ */ jsx32(
                "button",
                {
                  onClick: copySelection,
                  className: "px-2 py-1 text-[12px] text-slate-700 hover:bg-slate-50",
                  title: "Copy selection",
                  children: "Copy"
                }
              )
            ] })
          }
        ),
        /* @__PURE__ */ jsx32(
          ConfirmDeleteDialog,
          {
            isOpen: deleteOpen,
            onClose: () => setDeleteOpen(false),
            onConfirm: () => {
              setDeleteOpen(false);
              void doDeleteMessage();
            },
            title: "Delete message",
            message: "Are you sure you want to delete this message?",
            confirmText: "Delete",
            localStorageKey: "dopomogai:chat:skipMessageDeleteConfirm"
          }
        )
      ]
    }
  );
};

// src/themes/glass/GlassComposer.tsx
import { useMemo as useMemo19 } from "react";

// src/components/AddAttachmentButton.tsx
import { useEffect as useEffect14, useId, useRef as useRef10, useState as useState17 } from "react";
import { Plus as Plus3 } from "lucide-react";
import clsx28 from "clsx";
import { jsx as jsx33, jsxs as jsxs29 } from "react/jsx-runtime";
var AddAttachmentButton = ({
  onOpenKB,
  onUploadFromComputer,
  size = "md",
  variant = "ghost",
  className,
  menuPlacement = "bottom-start",
  title = "Add files"
}) => {
  const [open, setOpen] = useState17(false);
  const rootRef = useRef10(null);
  const timer = useRef10(null);
  const suppressClickRef = useRef10(false);
  const menuId = useId();
  const show = () => {
    if (timer.current) window.clearTimeout(timer.current);
    setOpen(true);
  };
  const hide = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(false), 120);
  };
  useEffect14(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);
  const btnSize = {
    sm: "h-9 w-9",
    md: "h-10 w-10",
    lg: "h-12 w-12"
  }[size];
  const iconSize = {
    sm: 18,
    md: 18,
    lg: 20
  }[size];
  const btnVariant = {
    ghost: "text-slate-400 hover:text-slate-600 hover:bg-slate-200/70",
    soft: "text-slate-600 bg-slate-100 hover:bg-slate-200",
    solid: "text-white bg-slate-800 hover:bg-slate-700"
  }[variant];
  const menuClassName = (() => {
    const base = "absolute z-30 min-w-[220px] py-1";
    const surface = "bg-white border border-slate-200 rounded-md shadow-lg";
    switch (menuPlacement) {
      case "bottom-end":
        return `${base} ${surface} right-0 top-[110%]`;
      case "top-start":
        return `${base} ${surface} left-0 bottom-[110%]`;
      case "top-end":
        return `${base} ${surface} right-0 bottom-[110%]`;
      case "bottom-start":
      default:
        return `${base} ${surface} left-0 top-[110%]`;
    }
  })();
  const item = "w-full text-left text-sm px-3 py-2 hover:bg-slate-50 active:bg-slate-100 transition-colors";
  return /* @__PURE__ */ jsxs29("div", { ref: rootRef, className: clsx28("relative inline-flex", className), onMouseEnter: show, onMouseLeave: hide, children: [
    /* @__PURE__ */ jsx33(
      "button",
      {
        type: "button",
        onClick: () => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          onOpenKB();
        },
        onPointerDown: (e) => {
          if (e.pointerType === "touch") {
            suppressClickRef.current = true;
            setOpen((prev) => !prev);
          }
        },
        onContextMenu: (e) => {
          e.preventDefault();
          setOpen(true);
        },
        onKeyDown: (e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
          if (e.key === "Escape") setOpen(false);
        },
        className: clsx28(
          "inline-flex items-center justify-center rounded-xl transition-colors select-none shrink-0",
          btnSize,
          btnVariant
        ),
        title,
        "aria-label": title,
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": menuId,
        children: /* @__PURE__ */ jsx33(Plus3, { size: iconSize })
      }
    ),
    open && /* @__PURE__ */ jsxs29("div", { id: menuId, className: menuClassName, role: "menu", "aria-label": "Add attachment menu", children: [
      /* @__PURE__ */ jsx33(
        "button",
        {
          className: item,
          onClick: () => {
            onUploadFromComputer();
            setOpen(false);
          },
          role: "menuitem",
          children: "Upload from computer"
        }
      ),
      /* @__PURE__ */ jsx33(
        "button",
        {
          className: item,
          onClick: () => {
            onOpenKB();
            setOpen(false);
          },
          role: "menuitem",
          children: "Add from Knowledge Base"
        }
      )
    ] })
  ] });
};

// src/themes/glass/GlassComposer.tsx
import { useChat as useChat5, useChatClient as useChatClient10 } from "@dopomogai/chat-react";

// src/composer/useComposerController.ts
import { useEffect as useEffect15, useMemo as useMemo18, useRef as useRef11, useState as useState18 } from "react";
import { useChatClient as useChatClient9, useFiles as useFiles3, useSessionDraft } from "@dopomogai/chat-react";
import { FilePurposeEnum as FilePurposeEnum3 } from "@dopomogai/types";
var useComposerController = (opts) => {
  const client = useChatClient9();
  const {
    onSend,
    isLoading = false,
    disabled = false,
    strings,
    capabilities,
    maxTextareaHeight = 200,
    enableCtrlEnterToSend = true
  } = opts;
  const caps = {
    enableKB: true,
    enableUpload: true,
    enableLibrary: true,
    enableGroups: true,
    ...capabilities || {}
  };
  const s = {
    placeholderDefault: "Ask anything...",
    placeholderWithAttachments: "Add a caption...",
    placeholderWhileLoading: "Regenerating...",
    addFilesTitle: "Add files",
    waitForUploadsTitle: "Wait for uploads to finish",
    ...strings || {}
  };
  const { text, attachments, setDraftText, addAttachment, updateAttachment, removeAttachment, clearDraft } = useSessionDraft();
  const textareaRef = useRef11(null);
  const fileInputRef = useRef11(null);
  const { uploadFile, isUploading, activeUploadsCount, getDownloadUrl } = useFiles3({
    onUploadSuccess: () => {
    },
    onUploadError: () => {
    }
  });
  const [isDragActive, setIsDragActive] = useState18(false);
  const [batch, setBatch] = useState18(null);
  const [openKB, setOpenKB] = useState18(false);
  const [openGroups, setOpenGroups] = useState18(false);
  const [openLibrary, setOpenLibrary] = useState18(false);
  useEffect15(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, maxTextareaHeight)}px`;
  }, [text, maxTextareaHeight]);
  const hasPendingUploads = useMemo18(
    () => attachments.some((a) => a.status === "queued" || a.status === "uploading"),
    [attachments]
  );
  const uploadAndAttach = async (files) => {
    if (!files.length || !caps.enableUpload) return;
    setBatch({ total: files.length, done: 0 });
    const pending = files.map((file) => {
      const isImg = file.type?.startsWith("image/");
      const clientId = crypto.randomUUID();
      const localUrl = isImg ? URL.createObjectURL(file) : void 0;
      return {
        clientId,
        fileId: void 0,
        url: localUrl || "",
        localUrl,
        name: file.name,
        type: isImg ? "image" : "file",
        status: "uploading",
        error: void 0,
        tokensEstimated: void 0
      };
    });
    for (const p of pending) addAttachment(p);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const p = pending[i];
      try {
        const uploaded = await uploadFile(file, FilePurposeEnum3.AgentInput);
        if (!uploaded) throw new Error("Upload failed");
        updateAttachment(p.clientId, {
          fileId: uploaded.id,
          url: getDownloadUrl(uploaded.id),
          status: "uploaded",
          error: void 0,
          tokensEstimated: uploaded.tokens_estimated ?? null,
          tokens_estimated: uploaded.tokens_estimated ?? null
        });
      } catch (e) {
        updateAttachment(p.clientId, {
          status: "error",
          error: e?.message || "Upload failed"
        });
      } finally {
        setBatch((prev) => prev ? { ...prev, done: prev.done + 1 } : prev);
      }
    }
    setTimeout(() => setBatch(null), 800);
  };
  const handleSubmit = (e) => {
    e?.preventDefault();
    const empty = !text.trim() && (attachments?.length ?? 0) === 0;
    if (empty || isLoading || disabled || isUploading || hasPendingUploads) return;
    onSend(text);
    clearDraft();
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };
  const handleTextareaKeyDown = (e) => {
    if (enableCtrlEnterToSend && e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const handleFileSelect = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    await uploadAndAttach(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const clickFilePicker = () => {
    if (!caps.enableUpload) return;
    fileInputRef.current?.click();
  };
  const attachFileIdsToDraft = (ids) => {
    if (!caps.enableKB) return;
    ids.forEach((fid) => {
      addAttachment({
        clientId: crypto.randomUUID(),
        fileId: fid,
        url: client ? `${client.config.apiBaseUrl}/api/v1/files/${fid}/download` : "",
        name: `file-${fid.substring(0, 6)}`,
        type: "file",
        status: "uploaded"
      });
    });
  };
  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (!caps.enableUpload) return;
    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length) void uploadAndAttach(files);
  };
  const isSendDisabled = useMemo18(() => {
    const empty = !text.trim() && (attachments?.length ?? 0) === 0;
    return empty || isLoading || disabled || isUploading || hasPendingUploads;
  }, [text, attachments, isLoading, disabled, isUploading, hasPendingUploads]);
  const sendDisabledReason = useMemo18(() => {
    const empty = !text.trim() && (attachments?.length ?? 0) === 0;
    if (empty) return "empty";
    if (isLoading) return "loading";
    if (disabled) return "disabled";
    if (isUploading) return "uploading";
    if (hasPendingUploads) return "pendingUploads";
    return null;
  }, [text, attachments, isLoading, disabled, isUploading, hasPendingUploads]);
  const placeholder = useMemo18(() => {
    if (attachments.length > 0) return s.placeholderWithAttachments;
    if (isLoading) return s.placeholderWhileLoading;
    return s.placeholderDefault;
  }, [attachments.length, isLoading, s.placeholderDefault, s.placeholderWhileLoading, s.placeholderWithAttachments]);
  return {
    text,
    setText: setDraftText,
    attachments,
    hasPendingUploads,
    isUploading,
    activeUploadsCount,
    batch,
    isDragActive,
    openKB,
    openLibrary,
    openGroups,
    setOpenKB: (v) => setOpenKB(v && caps.enableKB),
    setOpenLibrary: (v) => setOpenLibrary(v && caps.enableLibrary),
    setOpenGroups: (v) => setOpenGroups(v && caps.enableGroups),
    isSendDisabled,
    sendDisabledReason,
    textareaRef,
    fileInputRef,
    handleSubmit,
    handleTextareaKeyDown,
    handleFileSelect,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    clickFilePicker,
    attachFileIdsToDraft,
    removeAttachmentByClientId: removeAttachment,
    placeholder,
    waitForUploadsTitle: s.waitForUploadsTitle
  };
};

// src/themes/glass/glassComposerStyles.ts
import clsx29 from "clsx";
var getGlassComposerStyles = (params) => {
  const { isUploading, isDragActive } = params;
  return {
    shell: clsx29(
      "flex flex-col gap-0",
      "bg-white/80 backdrop-blur-xl border border-white/50",
      "rounded-3xl shadow-2xl shadow-slate-200/50",
      "transition-all duration-300 ease-out",
      "hover:shadow-slate-300/60 hover:bg-white/90",
      "ring-1 ring-slate-900/5",
      "focus-within:ring-2 focus-within:ring-blue-500/20",
      isUploading && "ring-2 ring-blue-400/50",
      isDragActive && "ring-2 ring-emerald-400/70 bg-white/90"
    ),
    attachmentsWrap: "px-4 pt-3 pb-2 border-b border-slate-100/50 mx-2",
    // Slightly taller row so the big button breathes
    inputRow: "flex items-center gap-2 p-2",
    textarea: "flex-1 bg-transparent border-none focus:ring-0 text-slate-800 placeholder:text-slate-400 py-4 px-1 min-h-[48px] max-h-[200px] resize-none text-[15px] leading-relaxed custom-scrollbar",
    sendButtonBase: "rounded-full flex items-center justify-center shrink-0 transition-all duration-200",
    // Stronger visual weight so the circle reads “big CTA”
    sendButtonEnabled: "bg-blue-600 text-white shadow-xl shadow-blue-500/25 hover:bg-blue-700 active:scale-95 ring-1 ring-white/30",
    sendButtonDisabled: "bg-slate-100 text-slate-300 cursor-not-allowed ring-1 ring-slate-200",
    stopButtonBase: "rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-all active:scale-95 shadow-xl",
    // Make LG clearly bigger (this is what you want)
    buttonSize: {
      sm: "h-[44px] w-[44px]",
      md: "h-[56px] w-[56px]",
      lg: "h-[72px] w-[72px]"
    },
    // Keep arrow readable (slightly larger at lg)
    sendIconSize: { sm: 20, md: 22, lg: 26 },
    stopIconSize: { sm: 18, md: 20, lg: 22 }
  };
};

// src/composer/components/ComposerSendButton.tsx
import clsx30 from "clsx";
import { ArrowUp, StopCircle } from "lucide-react";
import { jsx as jsx34 } from "react/jsx-runtime";
var ComposerSendButton = ({
  size,
  isLoading,
  isDisabled,
  disabledReason,
  onSend,
  onStop,
  waitForUploadsTitle,
  styles,
  className
}) => {
  const title = disabledReason === "pendingUploads" ? waitForUploadsTitle : void 0;
  if (isLoading) {
    return /* @__PURE__ */ jsx34(
      "button",
      {
        type: "button",
        onClick: onStop,
        className: clsx30(styles.stopBase, styles.buttonSize[size], className),
        "aria-label": "Stop generating",
        children: /* @__PURE__ */ jsx34(
          StopCircle,
          {
            size: styles.stopIconSize[size],
            fill: "currentColor",
            className: "text-white"
          }
        )
      }
    );
  }
  return /* @__PURE__ */ jsx34(
    "button",
    {
      type: "button",
      onClick: onSend,
      disabled: isDisabled,
      className: clsx30(
        styles.sendBase,
        styles.buttonSize[size],
        isDisabled ? styles.sendDisabled : styles.sendEnabled,
        className
      ),
      title,
      "aria-label": "Send message",
      children: /* @__PURE__ */ jsx34(ArrowUp, { size: styles.sendIconSize[size], strokeWidth: 2.5 })
    }
  );
};

// src/composer/components/ComposerAttachmentsTray.tsx
import { AnimatePresence as AnimatePresence2, motion as motion3 } from "framer-motion";
import { Fragment as Fragment9, jsx as jsx35, jsxs as jsxs30 } from "react/jsx-runtime";
var ComposerAttachmentsTray = ({
  attachments,
  onRemoveAttachment,
  isUploading,
  hasPendingUploads,
  batch,
  activeUploadsCount,
  className
}) => {
  return /* @__PURE__ */ jsx35(AnimatePresence2, { children: attachments.length > 0 && /* @__PURE__ */ jsxs30(
    motion3.div,
    {
      initial: { height: 0, opacity: 0 },
      animate: { height: "auto", opacity: 1 },
      exit: { height: 0, opacity: 0 },
      className,
      children: [
        /* @__PURE__ */ jsx35("div", { className: "flex flex-row gap-2 overflow-x-auto overflow-y-hidden custom-scrollbar pb-1", children: attachments.map((att) => /* @__PURE__ */ jsx35("div", { className: "flex-shrink-0 w-[420px] max-w-[85vw]", children: /* @__PURE__ */ jsx35(
          FileBubble,
          {
            fileId: att.fileId,
            url: att.url,
            previewUrl: att.localUrl,
            name: att.name,
            type: att.type,
            status: att.status,
            error: att.error,
            tokensEstimated: att.tokensEstimated ?? att.tokens_estimated,
            onRemove: () => onRemoveAttachment(att.clientId)
          }
        ) }, att.clientId)) }),
        (isUploading || hasPendingUploads) && /* @__PURE__ */ jsx35("div", { className: "mt-2 text-[11px] text-slate-500", children: batch ? /* @__PURE__ */ jsxs30(Fragment9, { children: [
          "Uploading ",
          batch.done,
          "/",
          batch.total,
          " files\u2026"
        ] }) : /* @__PURE__ */ jsxs30(Fragment9, { children: [
          "Uploading ",
          activeUploadsCount,
          " file",
          activeUploadsCount === 1 ? "" : "s",
          "\u2026"
        ] }) })
      ]
    }
  ) });
};

// src/themes/glass/GlassComposer.tsx
import { jsx as jsx36, jsxs as jsxs31 } from "react/jsx-runtime";
var GlassComposer = ({
  onSend,
  isLoading,
  disabled,
  capabilities,
  strings,
  size: sizeProp
}) => {
  const client = useChatClient10();
  const { activeId } = useChat5();
  const size = sizeProp || "lg";
  const vm = useComposerController({
    onSend,
    isLoading,
    disabled,
    strings: {
      placeholderDefault: "Ask anything...",
      placeholderWithAttachments: "Add a caption...",
      placeholderWhileLoading: "Regenerating...",
      ...strings || {}
    },
    capabilities: {
      // existing defaults (current behavior)
      enableKB: true,
      enableUpload: true,
      enableLibrary: false,
      enableGroups: false,
      ...capabilities || {}
    },
    enableCtrlEnterToSend: true,
    maxTextareaHeight: 200
  });
  const styles = getGlassComposerStyles({
    size,
    isUploading: vm.isUploading,
    isDragActive: vm.isDragActive
  });
  const bundleInfo = useMemo19(() => {
    const n = vm.attachments.length;
    if (n <= 3) return null;
    const totalTokens = vm.attachments.reduce(
      (acc, a) => acc + (Number(a.tokensEstimated ?? a.tokens_estimated ?? 0) || 0),
      0
    );
    return { count: n, tokens: totalTokens > 0 ? `~${Math.round(totalTokens).toLocaleString()} t` : null };
  }, [vm.attachments]);
  const enableKB = !!vm?.setOpenKB && (capabilities?.enableKB ?? true);
  const primaryAddAction = () => {
    if (capabilities?.enableKB === false) {
      vm.clickFilePicker();
      return;
    }
    vm.setOpenKB(true);
  };
  return /* @__PURE__ */ jsxs31(
    "div",
    {
      className: "w-full relative group",
      onDragEnter: vm.onDragEnter,
      onDragOver: vm.onDragOver,
      onDragLeave: vm.onDragLeave,
      onDrop: vm.onDrop,
      children: [
        /* @__PURE__ */ jsxs31("div", { className: styles.shell, children: [
          /* @__PURE__ */ jsx36(
            ComposerAttachmentsTray,
            {
              attachments: vm.attachments,
              onRemoveAttachment: vm.removeAttachmentByClientId,
              isUploading: vm.isUploading,
              hasPendingUploads: vm.hasPendingUploads,
              batch: vm.batch,
              activeUploadsCount: vm.activeUploadsCount,
              className: styles.attachmentsWrap
            }
          ),
          bundleInfo && /* @__PURE__ */ jsx36("div", { className: "px-6 -mt-2 pb-1 text-[11px] text-slate-500", children: /* @__PURE__ */ jsxs31("span", { className: "inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5", children: [
            "Bundle \u2022 ",
            bundleInfo.count,
            " files ",
            bundleInfo.tokens ? /* @__PURE__ */ jsxs31("span", { className: "text-slate-400", children: [
              "\u2022 ",
              bundleInfo.tokens
            ] }) : null
          ] }) }),
          /* @__PURE__ */ jsxs31("div", { className: styles.inputRow, children: [
            /* @__PURE__ */ jsx36(
              AddAttachmentButton,
              {
                onOpenKB: primaryAddAction,
                onUploadFromComputer: vm.clickFilePicker,
                size: "lg",
                variant: "ghost",
                className: "shrink-0",
                title: capabilities?.enableKB === false ? "Add files" : "Add files"
              }
            ),
            /* @__PURE__ */ jsx36("input", { type: "file", ref: vm.fileInputRef, className: "hidden", multiple: true, onChange: vm.handleFileSelect }),
            /* @__PURE__ */ jsx36(
              "textarea",
              {
                ref: vm.textareaRef,
                "data-composer-textarea": "true",
                value: vm.text,
                onChange: (e) => vm.setText(e.target.value),
                onKeyDown: vm.handleTextareaKeyDown,
                placeholder: vm.placeholder,
                rows: 1,
                disabled: !!isLoading || !!disabled,
                className: styles.textarea,
                style: { height: "auto" }
              }
            ),
            /* @__PURE__ */ jsx36(
              ComposerSendButton,
              {
                size,
                isLoading: !!isLoading,
                isDisabled: vm.isSendDisabled,
                disabledReason: vm.sendDisabledReason,
                waitForUploadsTitle: vm.waitForUploadsTitle,
                onSend: () => vm.handleSubmit(),
                onStop: () => {
                  if (activeId) client.streamHandler.disconnect(activeId);
                },
                styles: {
                  buttonSize: styles.buttonSize,
                  sendBase: styles.sendButtonBase,
                  sendEnabled: styles.sendButtonEnabled,
                  sendDisabled: styles.sendButtonDisabled,
                  stopBase: styles.stopButtonBase,
                  sendIconSize: styles.sendIconSize,
                  stopIconSize: styles.stopIconSize
                }
              }
            )
          ] })
        ] }),
        capabilities?.enableKB !== false && /* @__PURE__ */ jsx36(FullscreenModal, { isOpen: vm.openKB, onClose: () => vm.setOpenKB(false), title: "Add from Knowledge Base", children: /* @__PURE__ */ jsx36("div", { className: "bg-white rounded-md p-2", children: /* @__PURE__ */ jsx36(
          KBPickerPanel,
          {
            onAttach: (ids) => {
              vm.attachFileIdsToDraft(ids);
              vm.setOpenKB(false);
            }
          }
        ) }) })
      ]
    }
  );
};

// src/themes/glass/index.tsx
import { AlertCircle as AlertCircle3 } from "lucide-react";
import { jsx as jsx37, jsxs as jsxs32 } from "react/jsx-runtime";
var GlassTheme = {
  surface: "app",
  ChatWindow: GlassWindow,
  MessageBubble: GlassBubble,
  Composer: GlassComposer,
  components: {
    "file-bubble": FileBubble
  },
  TypingIndicator: () => /* @__PURE__ */ jsxs32("div", { className: "ml-2 md:ml-12 mb-4 flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest animate-pulse select-none", children: [
    /* @__PURE__ */ jsx37("span", { className: "w-2 h-2 bg-emerald-400 rounded-full" }),
    "Thinking..."
  ] }),
  ToolResult: ({ result, name }) => /* @__PURE__ */ jsx37("div", { className: "w-full", children: /* @__PURE__ */ jsx37(
    ToolBubble,
    {
      toolCallId: "history",
      name,
      args: { _info: "Arguments hidden in history view" },
      result,
      status: "success"
    }
  ) }),
  ErrorState: ({ error, onRetry }) => /* @__PURE__ */ jsxs32("div", { className: "mx-auto my-6 max-w-md p-4 rounded-xl border border-red-200 bg-red-50/90 backdrop-blur text-red-700 text-sm flex items-start gap-3 shadow-sm", children: [
    /* @__PURE__ */ jsx37(AlertCircle3, { size: 18, className: "mt-0.5 shrink-0" }),
    /* @__PURE__ */ jsxs32("div", { className: "flex-1 leading-relaxed", children: [
      /* @__PURE__ */ jsx37("span", { className: "font-semibold block mb-1", children: "System Error" }),
      /* @__PURE__ */ jsx37("div", { className: "mb-2", children: error }),
      onRetry && /* @__PURE__ */ jsx37(
        "button",
        {
          onClick: onRetry,
          className: "inline-flex items-center px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-bold hover:bg-red-700",
          children: "Retry"
        }
      )
    ] })
  ] })
};

// src/themes/studio/index.tsx
import clsx33 from "clsx";
import { X as X6 } from "lucide-react";

// src/themes/studio/StudioBubble.tsx
import { useEffect as useEffect16, useMemo as useMemo20, useRef as useRef12, useState as useState19 } from "react";
import clsx31 from "clsx";
import { Check as Check7 } from "lucide-react";
import { useChatClient as useChatClient11 } from "@dopomogai/chat-react";
import { jsx as jsx38, jsxs as jsxs33 } from "react/jsx-runtime";
var StudioBubble = ({ role, content, parts, isStreaming, timestamp, id }) => {
  const client = useChatClient11();
  const theme = useChatTheme();
  const isUser = role === "user";
  const surface = theme.surface || "studio";
  const [isEditing, setIsEditing] = useState19(false);
  const [editValue, setEditValue] = useState19(content || "");
  const [deleteOpen, setDeleteOpen] = useState19(false);
  const bodyRef = useRef12(null);
  const bubbleRef = useRef12(null);
  useEffect16(() => {
    if (!isEditing) setEditValue(content || "");
  }, [content, isEditing]);
  const handleSaveEdit = async () => {
    if (!id || !editValue.trim() || editValue === content) {
      setIsEditing(false);
      return;
    }
    await client.editMessage(id, editValue);
    setIsEditing(false);
  };
  const normalized = useMemo20(() => {
    return normalizeMessage({
      messageId: id || "legacy",
      role,
      parts,
      content,
      isStreaming,
      surface
    });
  }, [id, role, parts, content, isStreaming, surface]);
  const getMarkdown = () => normalized.toMarkdown();
  const getFormattedText = () => (bodyRef.current?.innerText || "").trim();
  const doDeleteMessage = async () => {
    if (id) client.deleteMessage(id);
  };
  const onDeleteClick = () => {
    if (shouldAskDeleteConfirm()) setDeleteOpen(true);
    else void doDeleteMessage();
  };
  const align = isUser ? "right" : "left";
  return /* @__PURE__ */ jsxs33("div", { className: clsx31("group flex w-full mb-6 relative", isUser ? "justify-end" : "justify-start"), children: [
    /* @__PURE__ */ jsxs33(
      "div",
      {
        ref: bubbleRef,
        "data-message-id": id || void 0,
        "data-message-root": "true",
        className: clsx31(
          "relative max-w-[90%] md:max-w-[85%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed shadow-sm transition-all min-w-0",
          isUser ? "bg-blue-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm",
          isEditing && "ring-2 ring-blue-400 ring-offset-2 w-full max-w-4xl bg-white border-blue-200 text-slate-900"
        ),
        children: [
          !isEditing && /* @__PURE__ */ jsxs33(
            "div",
            {
              className: clsx31(
                "text-[10px] font-medium tracking-wide mb-1 uppercase opacity-50 select-none",
                isUser ? "text-blue-100 text-right" : "text-slate-400"
              ),
              children: [
                isUser ? "You" : "Assistant",
                " \u2022",
                " ",
                timestamp ? new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Just now"
              ]
            }
          ),
          !isEditing && /* @__PURE__ */ jsx38(
            MessageHoverToolbars,
            {
              containerRef: bubbleRef,
              surface,
              align,
              getMarkdown,
              getFormattedText,
              defaultCopyMode: "formatted",
              allowEdit: true,
              onEdit: () => setIsEditing(true),
              allowDelete: true,
              onDelete: onDeleteClick
            }
          ),
          isEditing ? /* @__PURE__ */ jsxs33("div", { className: "flex flex-col gap-2", children: [
            /* @__PURE__ */ jsx38(
              "textarea",
              {
                value: editValue,
                onChange: (e) => setEditValue(e.target.value),
                className: "w-full bg-slate-50 border border-slate-200 rounded-md p-2 text-slate-800 focus:outline-none focus:ring-0 resize-none min-h-[150px] font-mono text-xs",
                autoFocus: true
              }
            ),
            /* @__PURE__ */ jsxs33("div", { className: "flex justify-end gap-2", children: [
              /* @__PURE__ */ jsx38("button", { onClick: () => setIsEditing(false), className: "px-3 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 rounded-md", children: "Cancel" }),
              /* @__PURE__ */ jsxs33(
                "button",
                {
                  onClick: handleSaveEdit,
                  className: "px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md flex items-center gap-1",
                  children: [
                    /* @__PURE__ */ jsx38(Check7, { size: 12 }),
                    " Save & Regenerate"
                  ]
                }
              )
            ] })
          ] }) : /* @__PURE__ */ jsxs33("div", { ref: bodyRef, className: "min-w-0 select-text", children: [
            /* @__PURE__ */ jsx38(
              MessageBody,
              {
                messageId: id || "legacy",
                role,
                parts,
                content,
                isStreaming,
                surface,
                blockRenderers: theme.blockRenderers
              }
            ),
            isStreaming && /* @__PURE__ */ jsx38("span", { className: "inline-block w-1.5 h-3.5 ml-1 bg-current align-middle animate-pulse" })
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx38(
      ConfirmDeleteDialog,
      {
        isOpen: deleteOpen,
        onClose: () => setDeleteOpen(false),
        onConfirm: () => {
          setDeleteOpen(false);
          void doDeleteMessage();
        },
        title: "Delete message",
        message: "Are you sure you want to delete this message?",
        confirmText: "Delete",
        localStorageKey: "dopomogai:chat:skipMessageDeleteConfirm"
      }
    )
  ] });
};

// src/themes/studio/StudioComposer.tsx
import clsx32 from "clsx";
import { Loader2 as Loader27, Send as Send2, StopCircle as StopCircle2 } from "lucide-react";
import { motion as motion4, AnimatePresence as AnimatePresence3 } from "framer-motion";
import { useChat as useChat6, useChatClient as useChatClient12 } from "@dopomogai/chat-react";
import { Fragment as Fragment10, jsx as jsx39, jsxs as jsxs34 } from "react/jsx-runtime";
var StudioComposer = ({ onSend, isLoading, placeholder, disabled }) => {
  const client = useChatClient12();
  const { activeId } = useChat6();
  const vm = useComposerController({
    onSend,
    isLoading,
    disabled,
    strings: {
      placeholderDefault: placeholder || "Send a message...",
      placeholderWithAttachments: "Add a caption...",
      placeholderWhileLoading: "Generating..."
    },
    capabilities: {
      enableKB: true,
      enableUpload: true,
      enableLibrary: false,
      enableGroups: false
    },
    maxTextareaHeight: 200,
    enableCtrlEnterToSend: true
  });
  return /* @__PURE__ */ jsxs34(
    "div",
    {
      className: "p-4 border-t border-slate-200 bg-white relative",
      onDragEnter: vm.onDragEnter,
      onDragOver: vm.onDragOver,
      onDragLeave: vm.onDragLeave,
      onDrop: vm.onDrop,
      children: [
        /* @__PURE__ */ jsx39(AnimatePresence3, { children: vm.attachments.length > 0 && /* @__PURE__ */ jsxs34(
          motion4.div,
          {
            initial: { height: 0, opacity: 0 },
            animate: { height: "auto", opacity: 1 },
            exit: { height: 0, opacity: 0 },
            className: "pb-3",
            children: [
              /* @__PURE__ */ jsx39("div", { className: "flex flex-row gap-2 overflow-x-auto overflow-y-hidden custom-scrollbar pb-1", children: vm.attachments.map((att) => /* @__PURE__ */ jsx39("div", { className: "flex-shrink-0 w-[420px] max-w-[85vw]", children: /* @__PURE__ */ jsx39(
                FileBubble,
                {
                  fileId: att.fileId,
                  url: att.url,
                  previewUrl: att.localUrl,
                  name: att.name,
                  type: att.type,
                  status: att.status,
                  error: att.error,
                  tokensEstimated: att.tokensEstimated ?? att.tokens_estimated,
                  onRemove: () => vm.removeAttachmentByClientId(att.clientId)
                }
              ) }, att.clientId)) }),
              (vm.isUploading || vm.hasPendingUploads) && /* @__PURE__ */ jsx39("div", { className: "mt-2 text-[11px] text-slate-400", children: vm.batch ? /* @__PURE__ */ jsxs34(Fragment10, { children: [
                "Uploading ",
                vm.batch.done,
                "/",
                vm.batch.total,
                " files\u2026"
              ] }) : /* @__PURE__ */ jsxs34(Fragment10, { children: [
                "Uploading ",
                vm.activeUploadsCount,
                " file",
                vm.activeUploadsCount === 1 ? "" : "s",
                "\u2026"
              ] }) })
            ]
          }
        ) }),
        /* @__PURE__ */ jsxs34("div", { className: "relative flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-2 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 transition-all shadow-inner", children: [
          /* @__PURE__ */ jsx39(
            AddAttachmentButton,
            {
              onOpenKB: () => vm.setOpenKB(true),
              onUploadFromComputer: vm.clickFilePicker,
              size: "md",
              variant: "ghost",
              className: "shrink-0"
            }
          ),
          /* @__PURE__ */ jsx39("input", { type: "file", ref: vm.fileInputRef, className: "hidden", onChange: vm.handleFileSelect, multiple: true }),
          /* @__PURE__ */ jsx39(
            "textarea",
            {
              ref: vm.textareaRef,
              value: vm.text,
              onChange: (e) => vm.setText(e.target.value),
              onKeyDown: vm.handleTextareaKeyDown,
              placeholder: vm.placeholder,
              disabled: isLoading || disabled,
              className: "flex-1 max-h-[200px] min-h-[24px] py-2 bg-transparent border-none focus:ring-0 resize-none text-sm text-slate-800 placeholder:text-slate-400 leading-relaxed",
              rows: 1
            }
          ),
          isLoading ? /* @__PURE__ */ jsx39(
            "button",
            {
              onClick: () => {
                if (activeId) client.streamHandler.disconnect(activeId);
              },
              className: "p-2 rounded-lg transition-all flex items-center justify-center bg-slate-800 text-white shadow-md hover:bg-slate-700 active:scale-95",
              title: "Stop generating",
              children: /* @__PURE__ */ jsx39(StopCircle2, { size: 18 })
            }
          ) : /* @__PURE__ */ jsx39(
            "button",
            {
              onClick: () => vm.handleSubmit(),
              disabled: vm.isSendDisabled,
              className: clsx32(
                "p-2 rounded-lg transition-all flex items-center justify-center",
                !vm.isSendDisabled ? "bg-blue-600 text-white shadow-md hover:bg-blue-700 active:scale-95" : "bg-slate-200 text-slate-400 cursor-not-allowed"
              ),
              title: vm.sendDisabledReason === "pendingUploads" ? vm.waitForUploadsTitle : void 0,
              children: vm.isUploading ? /* @__PURE__ */ jsx39(Loader27, { size: 18, className: "animate-spin" }) : /* @__PURE__ */ jsx39(Send2, { size: 18 })
            }
          )
        ] }),
        /* @__PURE__ */ jsx39(FullscreenModal, { isOpen: vm.openKB, onClose: () => vm.setOpenKB(false), title: "Add from Knowledge Base", children: /* @__PURE__ */ jsx39("div", { className: "bg-white rounded-md p-2", children: /* @__PURE__ */ jsx39(
          KBPickerPanel,
          {
            onAttach: (ids) => {
              vm.attachFileIdsToDraft(ids);
              vm.setOpenKB(false);
            }
          }
        ) }) })
      ]
    }
  );
};

// src/themes/studio/index.tsx
import { jsx as jsx40, jsxs as jsxs35 } from "react/jsx-runtime";
var StudioToolResult = ({ result, name }) => {
  return /* @__PURE__ */ jsx40("div", { className: "mb-6 pl-4 border-l-2 border-slate-100", children: /* @__PURE__ */ jsx40(
    ToolBubble,
    {
      toolCallId: "history",
      name,
      args: { _info: "Arguments hidden in history view" },
      result,
      status: "success"
    }
  ) });
};
var StudioTheme = {
  surface: "studio",
  MessageBubble: StudioBubble,
  Composer: StudioComposer,
  ChatWindow: ({ children, className }) => /* @__PURE__ */ jsx40("div", { className: clsx33("flex flex-col h-full bg-slate-50/50 font-sans", className), children }),
  TypingIndicator: () => /* @__PURE__ */ jsxs35("div", { className: "flex items-center gap-1.5 p-4 text-slate-400 text-xs font-medium animate-pulse", children: [
    /* @__PURE__ */ jsx40("span", { className: "w-1.5 h-1.5 bg-slate-400 rounded-full" }),
    /* @__PURE__ */ jsx40("span", { className: "w-1.5 h-1.5 bg-slate-400 rounded-full animation-delay-200" }),
    /* @__PURE__ */ jsx40("span", { className: "w-1.5 h-1.5 bg-slate-400 rounded-full animation-delay-400" }),
    /* @__PURE__ */ jsx40("span", { className: "ml-2", children: "Agent is thinking..." })
  ] }),
  ToolResult: StudioToolResult,
  ErrorState: ({ error }) => /* @__PURE__ */ jsxs35("div", { className: "m-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-800 text-sm animate-in slide-in-from-top-2", children: [
    /* @__PURE__ */ jsx40(X6, { size: 16 }),
    error
  ] })
};
export {
  ArtifactBlock,
  AttachmentGroupBlock,
  BlockActions,
  BlockFrame,
  ChatUIProvider,
  CompactActionBar,
  ConfirmDeleteDialog,
  CopyMenu,
  FileBubble,
  FullscreenModal,
  GenerativeUIRenderer,
  GlassTheme,
  MarkdownBlock,
  MessageBody,
  MessageHoverToolbars,
  MessageList,
  NeobrutalismTheme,
  ReasoningBlock,
  StudioTheme,
  ToolBubble,
  ToolCallBlock,
  ToolResultBlock,
  UIBlock,
  useChatTheme
};
